import { useState, useEffect, useRef, useCallback } from "react";
import { Plus, User, Building, FileText, Clock, Upload, Save, ArrowLeft, Home, Trash2, Edit, Search, Filter, File, Image, Users, X, Crown, Map, Download, Maximize, Minimize, ChevronDown, ChevronUp, Mail, Calendar } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { TodoManagement } from "@/components/TodoManagement";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { compressFile } from "@/lib/fileCompression";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useOrganizationTheme } from "@/hooks/useOrganizationTheme";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CustomerAppointmentDialog } from "@/components/customer/CustomerAppointmentDialog";
import { WinPerformanceDialog } from "@/components/d2d/WinPerformanceDialog";
import { Trophy } from "lucide-react";
interface Customer {
  id: string;
  type?: "new" | "existing";
  first_name: string;
  last_name: string | null;
  name?: string; // computed field for display
  birthDate?: string;
  customerNumber?: string;
  servicePin?: string;
  iban?: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  needsAnalysis?: File[];
  documents?: File[];
  protocol?: ProtocolEntry[];
  created_at?: string;
  updated_at?: string;
  user_id: string;
  taskCount?: number;
}
interface ProtocolEntry {
  id: string;
  date: string;
  time: string;
  note: string;
}
interface CustomerManagementProps {
  customerId?: string;
  onClose?: () => void; 
}

// SwipeableCustomerCard component
interface SwipeableCustomerCardProps {
  customer: Customer;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const SwipeableCustomerCard = ({ customer, onSelect, onEdit, onDelete }: SwipeableCustomerCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const currentXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isSwipingRef = useRef(false);
  const animationFrameRef = useRef<number>();

  const updateTransform = useCallback((x: number) => {
    if (cardRef.current && backgroundRef.current) {
      const clampedX = Math.min(0, Math.max(-80, x));
      cardRef.current.style.transform = `translateX(${clampedX}px)`;
      
      // Show delete background when swiping left
      const opacity = Math.abs(clampedX) / 80;
      backgroundRef.current.style.opacity = opacity.toString();
    }
  }, []);

  const resetPosition = useCallback(() => {
    if (cardRef.current && backgroundRef.current) {
      cardRef.current.style.transform = 'translateX(0px)';
      cardRef.current.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      backgroundRef.current.style.opacity = '0';
      
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.style.transition = '';
        }
      }, 300);
    }
  }, []);

  const handleStart = useCallback((clientX: number, clientY: number) => {
    startXRef.current = clientX;
    startYRef.current = clientY;
    currentXRef.current = 0;
    isDraggingRef.current = true;
    isSwipingRef.current = false;
  }, []);

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!isDraggingRef.current) return;
    
    const deltaX = clientX - startXRef.current;
    const deltaY = clientY - startYRef.current;
    
    // Only start swiping if movement is primarily horizontal and to the left
    if (!isSwipingRef.current) {
      const minSwipeDistance = 15;
      const maxVerticalDeviation = 30;
      
      if (Math.abs(deltaX) < minSwipeDistance) return;
      if (Math.abs(deltaY) > maxVerticalDeviation) return;
      if (deltaX > 0) return; // Only allow left swipes
      
      isSwipingRef.current = true;
      if (cardRef.current) {
        cardRef.current.style.transition = '';
      }
    }
    
    if (isSwipingRef.current) {
      currentXRef.current = deltaX;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      animationFrameRef.current = requestAnimationFrame(() => {
        updateTransform(deltaX);
      });
    }
  }, [updateTransform]);

  const handleEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    
    isDraggingRef.current = false;
    const wasSwipping = isSwipingRef.current;
    isSwipingRef.current = false;
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Trigger delete if swiped far enough
    if (wasSwipping && currentXRef.current < -40) {
      onDelete();
    }
    
    resetPosition();
  }, [resetPosition, onDelete]);

  // Mouse events
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleStart(e.clientX, e.clientY);
  }, [handleStart]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleMouseLeave = useCallback(() => {
    if (isDraggingRef.current) {
      handleEnd();
    }
  }, [handleEnd]);

  // Touch events
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      handleStart(touch.clientX, touch.clientY);
    }
  }, [handleStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      handleMove(touch.clientX, touch.clientY);
    }
  }, [handleMove]);

  const handleTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleCardClick = useCallback((e: React.MouseEvent) => {
    if (isDraggingRef.current || isSwipingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onSelect();
  }, [onSelect]);

  return (
    <Card className="hover:shadow-md transition-shadow w-full relative overflow-hidden">
      {/* Delete background */}
      <div
        ref={backgroundRef}
        className="absolute inset-0 bg-destructive flex items-center justify-end px-4 opacity-0"
      >
        <Trash2 className="h-5 w-5 text-destructive-foreground" />
      </div>
      
      {/* Card content */}
      <div
        ref={cardRef}
        className="relative bg-background"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}  
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <CardContent className="p-4 w-full min-w-0 cursor-pointer hover:bg-muted/50 transition-colors relative" onClick={handleCardClick}>
          <Button size="sm" variant="ghost" onClick={e => {
              e.stopPropagation();
              onEdit();
            }} className="absolute top-2 right-2 h-8 w-8 p-0 hover:bg-muted">
            <Edit className="h-4 w-4" />
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0 pr-10">
               <div className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-semibold">{customer.name}</h3>
                  {customer.taskCount !== undefined && customer.taskCount > 0 && (
                    <span className="bg-gradient-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded-full text-xs font-medium">
                      {customer.taskCount}
                    </span>
                  )}
                </div>
              <div className="text-sm text-muted-foreground space-y-1">
                {customer.email && <p className="truncate">📧 {customer.email}</p>}
                {customer.phone && <p>📞 <a href={`tel:${customer.phone}`} className="text-blue-600 hover:text-blue-800 hover:underline" onClick={e => e.stopPropagation()}>{customer.phone}</a></p>}
                {customer.address && <div className="text-sm text-gray-600 flex">
                    <span className="mr-2">📍</span>
                    <div className="leading-tight">
                      {(() => {
                        const parts = customer.address.split(', ');
                        if (parts.length >= 2) {
                          const street = parts[0].trim();
                          const cityPart = parts[1].trim();
                          const state = parts[2]?.trim() || "";
                          const country = parts[3]?.trim() || parts[2]?.trim() || "Deutschland";
                          const cityMatch = cityPart.match(/^(\d+)\s+(.+)$/);
                          if (cityMatch) {
                            const postalCode = cityMatch[1];
                            const city = cityMatch[2];
                            return <>
                                <div>{street}</div>
                                <div>{postalCode} {city}</div>
                                {state && <div>{state}</div>}
                                {country !== state && <div>{country}</div>}
                              </>;
                          } else {
                            return <div>{customer.address}</div>;
                          }
                        } else {
                          return <div>{customer.address}</div>;
                        }
                      })()}
                    </div>
                  </div>}
                {customer.birthDate && <p className="text-sm text-muted-foreground">🎂 {format(new Date(customer.birthDate), 'dd.MM.yyyy')}</p>}
                {customer.customerNumber && <p className="text-sm text-muted-foreground truncate">🆔 {customer.customerNumber}</p>}
              </div>
            </div>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

const CustomerManagement = ({ customerId: propCustomerId, onClose }: CustomerManagementProps = {}) => {
  const {
    toast
  } = useToast();
  const { organization } = useOrganizationTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Initialize sidebar state from localStorage, default to true (expanded)
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    birthDate: "",
    customerNumber: "",
    servicePin: "",
    iban: "",
    email: "",
    phone: ""
  });
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<Array<{
    displayName: string;
    fullAddress: string;
    street?: string;
    city?: string;
    state?: string;
    postcode?: string;
    isStreet?: boolean;
    isHouseNumber?: boolean;
  }>>([]);
  const [selectedStreet, setSelectedStreet] = useState<{
    street: string;
    city: string;
    state: string;
    postcode: string;
  } | null>(null);
  const [searchStep, setSearchStep] = useState<'street' | 'houseNumber'>('street');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [protocolNote, setProtocolNote] = useState("");
  const [needsFiles, setNeedsFiles] = useState<File[]>([]);
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [customerFiles, setCustomerFiles] = useState<Array<{
    id: string;
    file_name: string;
    file_path: string;
    file_type: string;
    file_size: number;
  }>>([]);
  const [previewFile, setPreviewFile] = useState<{
    name: string;
    url: string;
    type: string;
  } | null>(null);
  
  // Ref for address input field
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [showPerformanceDialog, setShowPerformanceDialog] = useState(false);
  const [ibanError, setIbanError] = useState("");
  const [ibanSuccess, setIbanSuccess] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalFilesToUpload, setTotalFilesToUpload] = useState(0);
  const [currentFileUploading, setCurrentFileUploading] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isTasksExpanded, setIsTasksExpanded] = useState(false);
  const [openTasksCount, setOpenTasksCount] = useState(0);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  // Touch/Swipe handling
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Swipe detection
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  // Monitor scroll position to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      // Show button only when scrolled down past approximately 500px
      setShowScrollToTop(scrollTop > 500 && customers.length > 3);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [customers.length]);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (customerFiles.length > 1) {
      if (isLeftSwipe) {
        navigateToFile('next');
      } else if (isRightSwipe) {
        navigateToFile('prev');
      }
    }
  };

  // Function to parse address into components
  const parseAddress = (address: string) => {
    if (!address) return null;

    // Try to parse German address format: "Street Number, Postcode City, State, Country"
    const parts = address.split(',').map(part => part.trim());
    if (parts.length >= 2) {
      // Extract street and house number from first part
      const streetPart = parts[0];
      const streetMatch = streetPart.match(/^(.+?)(\s+\d+[a-zA-Z]*)?\s*$/);
      const street = streetMatch ? streetMatch[1].trim() : streetPart;
      const houseNumber = streetMatch && streetMatch[2] ? streetMatch[2].trim() : '';

      // Extract postcode and city from second part
      const cityPart = parts[1];
      const cityMatch = cityPart.match(/^(\d{5})\s+(.+)$/);
      const postcode = cityMatch ? cityMatch[1] : '';
      const city = cityMatch ? cityMatch[2] : cityPart;

      // State and country from remaining parts
      const state = parts[2] || '';
      const country = parts[3] || '';
      return {
        street,
        houseNumber,
        postcode,
        city,
        state,
        country,
        hasStructuredData: !!(street && (postcode || city))
      };
    }
    return {
      original: address,
      hasStructuredData: false
    };
  };

  // Function to open navigation app with address
  const openNavigation = (address: string) => {
    if (!address) return;
    const encodedAddress = encodeURIComponent(address);

    // Check if we're on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      // Try to open native navigation apps first
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        // Try Apple Maps first, fallback to Google Maps
        window.location.href = `maps://maps.google.com/maps?q=${encodedAddress}`;
      } else {
        // Android - try Google Maps intent
        window.location.href = `geo:0,0?q=${encodedAddress}`;
      }
    } else {
      // Desktop - open Google Maps in new tab
      window.open(`https://maps.google.com/maps?q=${encodedAddress}`, '_blank');
    }
  };

  // IBAN validation function
  const validateIBAN = (iban: string): boolean => {
    // Remove spaces and convert to uppercase
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();

    // Check length (15-34 characters for international IBANs)
    if (cleanIban.length < 15 || cleanIban.length > 34) {
      return false;
    }

    // Check format: starts with 2 letters followed by 2 digits
    if (!/^[A-Z]{2}[0-9]{2}/.test(cleanIban)) {
      return false;
    }

    // Move first 4 characters to end
    const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);

    // Replace letters with numbers (A=10, B=11, ..., Z=35)
    const numericString = rearranged.replace(/[A-Z]/g, letter => (letter.charCodeAt(0) - 55).toString());

    // Calculate mod 97
    let remainder = 0;
    for (let i = 0; i < numericString.length; i++) {
      remainder = (remainder * 10 + parseInt(numericString[i])) % 97;
    }
    return remainder === 1;
  };
  const handleIbanChange = (value: string) => {
    // Limit to 34 characters maximum
    const limitedValue = value.slice(0, 34);
    setFormData(prev => ({
      ...prev,
      iban: limitedValue
    }));
    if (limitedValue.trim() === "") {
      setIbanError("");
      setIbanSuccess("");
      return;
    }
    if (!validateIBAN(limitedValue)) {
      setIbanError("Ungültige IBAN. Bitte prüfen Sie das Format.");
      setIbanSuccess("");
    } else {
      setIbanError("");
      setIbanSuccess("✓ Gültige IBAN");
    }
  };

  // Navigation function for file preview
  const navigateToFile = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev' ? (currentFileIndex - 1 + customerFiles.length) % customerFiles.length : (currentFileIndex + 1) % customerFiles.length;
    const newFile = customerFiles[newIndex];
    if (newFile) {
      previewFile && URL.revokeObjectURL(previewFile.url);
      supabase.storage.from('customer-files').download(newFile.file_path).then(({
        data,
        error
      }) => {
        if (!error && data) {
          const url = URL.createObjectURL(data);
          setPreviewFile({
            name: newFile.file_name,
            url: url,
            type: newFile.file_type
          });
          setCurrentFileIndex(newIndex);
        }
      });
    }
  };

  // Direct file upload for customer details modal
  const handleDirectFileUpload = async (files: FileList | null, customerId: string) => {
    if (!files || files.length === 0) return;
    
    const filesArray = Array.from(files);
    setTotalFilesToUpload(filesArray.length);
    setCurrentFileUploading(0);
    setUploadProgress(0);
    setIsUploading(true);
    
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein, um Dateien hochzuladen.",
        variant: "destructive"
      });
      setIsUploading(false);
      return;
    }
    
    try {
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        setCurrentFileUploading(i + 1);
        
        // Progress: Starting file processing (25% per file step)
        setUploadProgress(Math.round((i / filesArray.length) * 100 + 25));
        
        // Skip compression for small files (< 1MB) to speed up upload
        let processedFile: File | Blob = file;
        if (file.size > 1024 * 1024) {
          // Progress: Compressing file (50% per file step)
          setUploadProgress(Math.round((i / filesArray.length) * 100 + 50));
          const compressedFileData = await compressFile(file);
          processedFile = compressedFileData.compressedFile;
        }
        
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${session.user.id}/${customerId}/${fileName}`;

        // Progress: Uploading file (75% per file step)
        setUploadProgress(Math.round((i / filesArray.length) * 100 + 75));

        // Upload file to storage
        const {
          error: uploadError
        } = await supabase.storage.from('customer-files').upload(filePath, processedFile);
        
        if (!uploadError) {
          // Save file info to database
          const {
            error: dbError
          } = await supabase.from('customer_files').insert({
            customer_id: customerId,
            user_id: session.user.id,
            file_name: file.name,
            file_path: filePath,
            file_type: file.type,
            file_size: processedFile.size
          });
          if (dbError) {
            console.error('Database error:', dbError);
          }
        } else {
          console.error('Upload error:', uploadError);
        }
        
        // Update progress - completed file
        const progress = Math.round(((i + 1) / filesArray.length) * 100);
        setUploadProgress(progress);
      }

      // Reload customer files
      loadCustomerFiles(customerId);
      
      toast({
        title: "Erfolg",
        description: `${filesArray.length} Datei(en) erfolgreich hochgeladen.`
      });
    } catch (error) {
      console.error('Error uploading files:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Hochladen der Dateien.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentFileUploading(0);
      setTotalFilesToUpload(0);
    }
  };

  // Load customer files
  const loadCustomerFiles = async (customerId: string) => {
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return;
      const {
        data,
        error
      } = await supabase.from('customer_files').select('*').eq('customer_id', customerId).eq('user_id', session.user.id).order('created_at', {
        ascending: false
      });
      if (error) {
        console.error('Error loading customer files:', error);
      } else {
        setCustomerFiles(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Load customer files when selectedCustomer changes
  useEffect(() => {
    if (selectedCustomer && !isEditingCustomer) {
      loadCustomerFiles(selectedCustomer.id);
    }
  }, [selectedCustomer, isEditingCustomer]);

  // Load customers from Supabase
  useEffect(() => {
    loadCustomers();
  }, []);

  // Auto-select customer from props or URL parameter
  useEffect(() => {
    const customerId = propCustomerId || searchParams.get('customerId');
    if (customerId && customers.length > 0 && !selectedCustomer) {
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
  }, [customers, searchParams, selectedCustomer, propCustomerId]);

  // Enhanced 2-step geocoding function with PLZ and city filtering
  const searchStreets = async (query: string) => {
    if (!query.trim() || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    setIsLoadingAddress(true);
    try {
      // Erkenne PLZ im Query (z.B. "Straße 761" oder "Straße, 761" oder "Straße 76135")
      const plzPattern = /(\d{2,5})/;
      const plzMatch = query.match(plzPattern);
      const extractedPLZ = plzMatch ? plzMatch[1] : null;
      
      // Erkenne Stadt/Ort im Query (z.B. "Straße Karlsruhe" oder "Straße, Karlsruhe")
      // Extrahiere mögliche Stadtnamen (mind. 3 Buchstaben, keine Zahlen)
      const cityPattern = /[,\s]+([a-zäöüß]{3,}(?:\s+[a-zäöüß]+)*)\s*$/i;
      const cityMatch = query.match(cityPattern);
      const extractedCity = cityMatch ? cityMatch[1].trim() : null;
      
      // Entferne PLZ und Stadt aus Query für bessere Straßensuche
      let streetQuery = query;
      if (extractedPLZ) {
        streetQuery = streetQuery.replace(plzPattern, '').trim();
      }
      if (extractedCity) {
        streetQuery = streetQuery.replace(cityPattern, '').trim();
      }
      streetQuery = streetQuery.replace(/,\s*$/, '').trim();
      
      const searchQuery = `${streetQuery}, Deutschland`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&addressdetails=1&limit=50&countrycodes=de&featuretype=way`
      );
      
      if (response.ok) {
        const results = await response.json();

        // Group streets by state and city with PLZ and city priority
        const streetMap: Record<string, any> = {};
        
        for (const result of results) {
          const address = result.address;
          const street = address?.road;
          const city = address?.city || address?.town || address?.village || address?.hamlet || address?.municipality || '';
          const state = address?.state || '';
          const postcode = address?.postcode || '';
          
          if (street && city && state) {
            // Wenn PLZ eingegeben wurde, nur Ergebnisse mit passender PLZ
            if (extractedPLZ && postcode && !postcode.startsWith(extractedPLZ)) {
              continue;
            }
            
            // Wenn Stadt eingegeben wurde, nur Ergebnisse mit passender Stadt
            if (extractedCity && !city.toLowerCase().includes(extractedCity.toLowerCase())) {
              continue;
            }
            
            const key = `${street}-${postcode}-${city}-${state}`;
            if (!streetMap[key]) {
              const plzMatch = extractedPLZ && postcode ? postcode.startsWith(extractedPLZ) : false;
              const cityMatch = extractedCity ? city.toLowerCase().includes(extractedCity.toLowerCase()) : false;
              
              streetMap[key] = {
                street,
                city,
                state,
                postcode,
                displayName: `${street}, ${postcode ? postcode + ' ' : ''}${city}, ${state}`,
                fullAddress: `${street}, ${postcode ? postcode + ' ' : ''}${city}, ${state}, Deutschland`,
                isStreet: true,
                plzMatch,
                cityMatch,
                matchScore: (plzMatch ? 2 : 0) + (cityMatch ? 2 : 0)
              };
            }
          }
        }
        
        // Sortiere: Beste Matches zuerst (PLZ + Stadt), dann nach PLZ, dann nach Stadt
        const suggestions = Object.values(streetMap).sort((a: any, b: any) => {
          // Sortiere nach Match-Score (höher = besser)
          if (a.matchScore !== b.matchScore) {
            return b.matchScore - a.matchScore;
          }
          
          // Bei gleichem Score: nach PLZ, dann nach Stadt sortieren
          if (a.postcode !== b.postcode) {
            return a.postcode.localeCompare(b.postcode);
          }
          return a.city.localeCompare(b.city);
        });
        
        // Limitiere auf Top 15 Ergebnisse
        setAddressSuggestions(suggestions.slice(0, 15) as any);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Straßen:', error);
    } finally {
      setIsLoadingAddress(false);
    }
  };
  const searchHouseNumbers = async (street: string, city: string, state: string, postcode: string) => {
    console.log('searchHouseNumbers called with:', {
      street,
      city,
      state,
      postcode
    });
    setIsLoadingAddress(true);
    try {
      console.log('Starting house number search...');
      // Search for all addresses on this street to get real house numbers
      const streetQuery = `${street}, ${city}, ${state}, Deutschland`;
      const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(streetQuery)}&format=json&addressdetails=1&limit=100&countrycodes=de`);
      if (response.ok) {
        const results = await response.json();
        console.log('API response:', results.length, 'results');

        // Extract unique house numbers for this exact street
        const houseNumbers = new Set();
        const suggestions = [];
        for (const result of results) {
          const address = result.address;
          const resultStreet = address?.road;
          const resultCity = address?.city || address?.town || address?.village;
          const houseNumber = address?.house_number;
          console.log('Checking result:', {
            resultStreet,
            resultCity,
            houseNumber,
            expectedStreet: street,
            expectedCity: city
          });

          // More flexible matching - check if street name is contained
          if (houseNumber && resultStreet && resultStreet.toLowerCase().includes(street.toLowerCase()) && resultCity && resultCity.toLowerCase() === city.toLowerCase() && !houseNumbers.has(houseNumber)) {
            houseNumbers.add(houseNumber);
            const fullAddress = `${street} ${houseNumber}, ${postcode ? postcode + ' ' : ''}${city}, ${state}, Deutschland`;
            suggestions.push({
              displayName: houseNumber,
              fullAddress: fullAddress,
              street,
              city,
              state,
              postcode,
              isHouseNumber: true
            });
          }
        }
        console.log('Found house numbers:', Array.from(houseNumbers));

        // If no real house numbers found, create common suggestions as fallback
        if (suggestions.length === 0) {
          console.log('No house numbers found via API, creating fallback suggestions');
          for (let i = 1; i <= 50; i++) {
            suggestions.push({
              displayName: i.toString(),
              fullAddress: `${street} ${i}, ${postcode ? postcode + ' ' : ''}${city}, ${state}, Deutschland`,
              street,
              city,
              state,
              postcode,
              isHouseNumber: true
            });
          }
        }

        // Sort all suggestions intelligently
        suggestions.sort((a, b) => {
          const aNum = a.displayName;
          const bNum = b.displayName;

          // Extract main number and suffix for proper sorting
          const parseHouseNumber = (num: string) => {
            const match = num.match(/^(\d+)(.*)$/);
            if (match) {
              return {
                main: parseInt(match[1]),
                suffix: match[2] || ''
              };
            }
            return {
              main: 0,
              suffix: num
            };
          };
          const aParsed = parseHouseNumber(aNum);
          const bParsed = parseHouseNumber(bNum);

          // First sort by main number
          if (aParsed.main !== bParsed.main) {
            return aParsed.main - bParsed.main;
          }

          // Then sort by suffix
          return aParsed.suffix.localeCompare(bParsed.suffix);
        });
        console.log('Found house number suggestions:', suggestions.length);

        // Add option to continue without house number at the beginning
        suggestions.unshift({
          displayName: "Ohne Hausnummer fortfahren",
          fullAddress: `${street}, ${postcode ? postcode + ' ' : ''}${city}, ${state}, Deutschland`,
          street,
          city,
          state,
          postcode,
          isHouseNumber: false
        });
        console.log('Setting address suggestions:', suggestions);
        setAddressSuggestions(suggestions);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Hausnummern:', error);
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Auto-search when address is entered
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (formData.address && formData.address.length >= 3) {
        if (searchStep === 'street') {
          searchStreets(formData.address);
        } else if (searchStep === 'houseNumber' && selectedStreet) {
          // Wenn Benutzer Hausnummer tippt, automatisch vollständige Adresse abrufen
          const addressPattern = /^(.+)\s+(\d+[a-zA-Z]?)$/;
          const match = formData.address.match(addressPattern);
          
          if (match && match[2]) {
            const houseNumber = match[2];
            try {
              const searchQuery = `${selectedStreet.street} ${houseNumber}, ${selectedStreet.postcode}, ${selectedStreet.city}, Deutschland`;
              const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&addressdetails=1&limit=1&countrycodes=de`);
              
              if (response.ok) {
                const results = await response.json();
                if (results.length > 0) {
                  const result = results[0];
                  const addr = result.address;
                  const street = addr?.road || selectedStreet.street;
                  const resultHouseNumber = addr?.house_number || houseNumber;
                  const city = addr?.city || addr?.town || addr?.village || selectedStreet.city;
                  const postcode = addr?.postcode || selectedStreet.postcode;
                  const state = addr?.state || selectedStreet.state;
                  
                  const completeAddress = `${street} ${resultHouseNumber}, ${postcode} ${city}, ${state}, Deutschland`;
                  setFormData(prev => ({ ...prev, address: completeAddress }));
                  setAddressSuggestions([]);
                  setSelectedStreet(null);
                  setSearchStep('street');
                }
              }
            } catch (error) {
              console.error('Fehler beim Abrufen der Adresse:', error);
            }
          }
        }
      } else if (formData.address.length < 3) {
        setAddressSuggestions([]);
        setSelectedStreet(null);
        setSearchStep('street');
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.address, searchStep, selectedStreet]);
  const selectAddressSuggestion = (suggestion: any) => {
    console.log('selectAddressSuggestion called with:', suggestion);
    if (suggestion.isStreet) {
      // User selected a street, now prepare for house number input
      console.log('Selected street, switching to house number input');
      setSelectedStreet({
        street: suggestion.street,
        city: suggestion.city,
        state: suggestion.state,
        postcode: suggestion.postcode
      });
      setSearchStep('houseNumber');
      
      // Set address to "Street " (with space) for immediate typing
      const addressWithSpace = `${suggestion.street} `;
      setFormData(prev => ({
        ...prev,
        address: addressWithSpace
      }));
      setAddressSuggestions([]);
      
      // Focus input and position cursor at the end
      setTimeout(() => {
        if (addressInputRef.current) {
          addressInputRef.current.focus();
          addressInputRef.current.setSelectionRange(addressWithSpace.length, addressWithSpace.length);
        }
      }, 0);
    } else {
      // User selected a house number or chose to continue without one
      console.log('Selected house number or continuing without one');
      setFormData(prev => ({
        ...prev,
        address: suggestion.fullAddress
      }));
      setAddressSuggestions([]);
      setSelectedStreet(null);
      setSearchStep('street');
    }
  };
  const resetAddressSearch = () => {
    setSelectedStreet(null);
    setSearchStep('street');
    setAddressSuggestions([]);
    setFormData(prev => ({
      ...prev,
      address: ''
    }));
  };
  const loadCustomers = async () => {
    console.log('🔍 loadCustomers called');
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      console.log('🔍 Current session:', session?.user?.email, 'user_id:', session?.user?.id);
      if (!session) {
        console.log('🔍 No session found');
        setLoading(false);
        return;
      }
      console.log('Loading customers from database...');
      const {
        data,
        error
      } = await supabase.from('customers').select('id, user_id, first_name, last_name, email, phone, address, notes, customer_number, service_pin, iban, birth_date, created_at, updated_at').eq('user_id', session.user.id).order('created_at', {
        ascending: false
      });
      console.log('Raw data from database:', data);
      if (error) {
        console.error('Error loading customers:', error);
        toast({
          title: "Fehler",
          description: "Kunden konnten nicht geladen werden.",
          variant: "destructive"
        });
      } else {
        // Load task counts for all customers
        const { data: todosData } = await supabase
          .from('todos')
          .select('customer_id')
          .eq('completed', false)
          .in('customer_id', (data || []).map(c => c.id));
        
        // Count tasks per customer
        const taskCounts = (todosData || []).reduce((acc, todo) => {
          acc[todo.customer_id] = (acc[todo.customer_id] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        
        // Convert database format to component format
        const convertedCustomers = (data || []).map(customer => {
          const extractFromNotes = (notes: string | null) => {
            if (!notes) return {
              birthDate: "",
              customerNumber: "",
              servicePin: "",
              iban: ""
            };
            const birthDateMatch = notes.match(/Geburtsdatum:\s*([^,]+)/);
            const customerNumberMatch = notes.match(/Kundennummer:\s*([^,]+)/);
            const servicePinMatch = notes.match(/Service-PIN:\s*([^,]+)/);
            const ibanMatch = notes.match(/IBAN:\s*([^,]+)/);
            return {
              birthDate: birthDateMatch ? birthDateMatch[1].trim() : "",
              customerNumber: customerNumberMatch ? customerNumberMatch[1].trim() : "",
              servicePin: servicePinMatch ? servicePinMatch[1].trim() : "",
              iban: ibanMatch ? ibanMatch[1].trim() : ""
            };
          };
          const extracted = extractFromNotes(customer.notes);
          return {
            id: customer.id,
            first_name: customer.first_name,
            last_name: customer.last_name,
            name: `${customer.first_name} ${customer.last_name || ""}`.trim(),
            birthDate: customer.birth_date || extracted.birthDate,
            customerNumber: customer.customer_number || extracted.customerNumber,
            servicePin: customer.service_pin || extracted.servicePin,
            iban: customer.iban || extracted.iban,
            email: customer.email || "",
            phone: customer.phone || "",
            address: customer.address || "",
            notes: customer.notes || "",
            protocol: [],
            created_at: customer.created_at,
            updated_at: customer.updated_at,
            user_id: customer.user_id,
            taskCount: taskCounts[customer.id] || 0
          };
        });
        console.log('Converted customers:', convertedCustomers);
        setCustomers(convertedCustomers);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTaskCount = async (customer: Customer) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || !customer) {
        setOpenTasksCount(0);
        return;
      }

      const { data, error } = await supabase
        .from('todos')
        .select('*')
        .eq('customer_id', customer.id)
        .eq('completed', false);

      if (error) {
        console.error('Error loading task count:', error);
        setOpenTasksCount(0);
      } else {
        setOpenTasksCount(data?.length || 0);
      }
    } catch (error) {
      console.error('Error:', error);
      setOpenTasksCount(0);
    }
  };

  // Load task count when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      loadTaskCount(selectedCustomer);
    } else {
      setOpenTasksCount(0);
    }
  }, [selectedCustomer]);

  const handleCreateCustomer = async () => {
    if (!formData.firstName || !formData.lastName || !formData.address || !formData.birthDate) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie alle Pflichtfelder aus.",
        variant: "destructive"
      });
      return;
    }
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein, um Kunden zu erstellen.",
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }
    try {
      const {
        data,
        error
      } = await supabase.from('customers').insert({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        address: formData.address.trim(),
        notes: null,
        customer_number: formData.customerNumber.trim() || null,
        service_pin: formData.servicePin.trim() || null,
        iban: formData.iban.trim() || null,
        birth_date: formData.birthDate || null,
        user_id: session.user.id
      }).select().single();
      if (error) {
        console.error('Error creating customer:', error);
        toast({
          title: "Fehler",
          description: `Kunde konnte nicht erstellt werden: ${error.message}`,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      // Upload files to Supabase Storage
      const allFiles = [...needsFiles, ...documentFiles];
      if (allFiles.length > 0) {
        for (const file of allFiles) {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
          const filePath = `${session.user.id}/${data.id}/${fileName}`;

          // Upload file to storage
          const {
            error: uploadError
          } = await supabase.storage.from('customer-files').upload(filePath, file);
          if (!uploadError) {
            // Save file info to database
            await supabase.from('customer_files').insert({
              customer_id: data.id,
              user_id: session.user.id,
              file_name: file.name,
              file_path: filePath,
              file_type: file.type,
              file_size: file.size
            });
          }
        }
      }

      // Convert to component format and add to state  
      const newCustomer: Customer = {
        id: data.id,
        user_id: data.user_id,
        first_name: data.first_name,
        last_name: data.last_name || "",
        name: `${data.first_name} ${data.last_name || ""}`.trim(),
        email: data.email,
        phone: data.phone,
        address: data.address,
        notes: data.notes,
        customerNumber: data.customer_number || "",
        servicePin: data.service_pin || "",
        iban: data.iban || "",
        birthDate: data.birth_date || "",
        created_at: data.created_at,
        updated_at: data.updated_at
      };
      setCustomers(prev => [newCustomer, ...prev]);
      setIsCreating(false);
      setFormData({
        firstName: "",
        lastName: "",
        address: "",
        birthDate: "",
        customerNumber: "",
        servicePin: "",
        iban: "",
        email: "",
        phone: ""
      });
      setNeedsFiles([]);
      setDocumentFiles([]);
      setSelectedCustomer(newCustomer);
      toast({
        title: "Kunde erstellt",
        description: `${newCustomer.name} wurde erfolgreich angelegt.`
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const addProtocolEntry = async (customerId: string) => {
    if (!protocolNote.trim()) return;
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return;
      const customer = customers.find(c => c.id === customerId);
      if (!customer) return;
      const currentNotes = customer.notes || "";
      const newNote = `${new Date().toLocaleString('de-DE')}: ${protocolNote}`;
      const updatedNotes = currentNotes ? `${currentNotes}\n\n${newNote}` : newNote;
      const {
        error
      } = await supabase.from('customers').update({
        notes: updatedNotes
      }).eq('id', customerId).eq('user_id', session.user.id);
      if (error) {
        console.error('Error updating notes:', error);
        toast({
          title: "Fehler",
          description: "Notiz konnte nicht gespeichert werden.",
          variant: "destructive"
        });
        return;
      }

      // Update local state
      const updatedCustomer = {
        ...customer,
        notes: updatedNotes
      };
      setCustomers(prev => prev.map(c => c.id === customerId ? updatedCustomer : c));
      if (selectedCustomer && selectedCustomer.id === customerId) {
        setSelectedCustomer(updatedCustomer);
      }
      setProtocolNote("");
      toast({
        title: "Notiz gespeichert",
        description: "Die Notiz wurde erfolgreich hinzugefügt."
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive"
      });
    }
  };

  const handleSendContactInfo = async (customer: Customer) => {
    if (!customer.email) {
      toast({
        title: "Keine E-Mail-Adresse",
        description: "Der Kunde hat keine E-Mail-Adresse hinterlegt.",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Fehler",
          description: "Sie müssen angemeldet sein.",
          variant: "destructive"
        });
        return;
      }

      // Fetch profile and organization data including logo
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name, phone, organization_id, avatar_url')
        .eq('user_id', session.user.id)
        .single();

      if (profileError) throw profileError;

      let organizationName = '';
      let organizationLogo = '';
      let organizationColor = '#3b82f6'; // Default blue
      if (profileData?.organization_id) {
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('name, logo_url, color')
          .eq('id', profileData.organization_id)
          .single();

        if (!orgError && orgData) {
          organizationName = orgData.name;
          organizationColor = orgData.color || '#3b82f6';
          // Convert logo_url to full public URL if it exists
          if (orgData.logo_url) {
            const { data: publicUrlData } = supabase.storage
              .from('organization-logos')
              .getPublicUrl(orgData.logo_url);
            organizationLogo = publicUrlData.publicUrl;
          }
        }
      }

      const advisorLastName = profileData?.last_name || '';
      const fullName = `${profileData?.first_name || ''} ${profileData?.last_name || ''}`.trim();
      
      // Get advisor avatar URL - it's already a full URL in the database
      const advisorAvatarUrl = profileData?.avatar_url || '';

      // Generate vCard for contact saving as email attachment
      const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${fullName}
N:${profileData?.last_name || ''};${profileData?.first_name || ''};;;
${organizationName ? `ORG:${organizationName}` : ''}
${profileData?.phone ? `TEL;TYPE=CELL:${profileData.phone}` : ''}
${session.user.email ? `EMAIL:${session.user.email}` : ''}
END:VCARD`.replace(/\n\n+/g, '\n'); // Remove empty lines

      // Generate vCard download URL
      const vCardDownloadUrl = `https://puwkrtqzszjsjkxowvin.supabase.co/functions/v1/get-advisor-vcard?userId=${session.user.id}`;

      // Call the edge function to send email with vCard attachment
      const { error: emailError } = await supabase.functions.invoke('send-email', {
        body: {
          to: customer.email,
          fromName: organizationName || undefined,
          subject: `Kontaktdaten - ${advisorLastName}`,
          attachments: [
            {
              filename: `${advisorLastName.replace(/\s+/g, '_')}_Kontakt.vcf`,
              content: vCardData,
              contentType: 'text/vcard'
            }
          ],
          html: `
            <!DOCTYPE html>
            <html lang="de">
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <meta name="color-scheme" content="light dark">
              <meta name="supported-color-schemes" content="light dark">
              <style>
                :root { color-scheme: light dark; }
                body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; }
                .email-container { max-width: 600px; margin: 0 auto; }
                .email-header { background: linear-gradient(135deg, ${organizationColor}dd 0%, ${organizationColor} 100%); padding: 40px 30px; text-align: center; }
                .email-logo { max-width: 80px; height: auto; margin-bottom: 10px; }
                .email-content { padding: 40px 30px; }
                .greeting { font-size: 18px; margin-bottom: 20px; line-height: 1.6; }
                .contact-box { border-left: 4px solid ${organizationColor}; padding: 24px; border-radius: 8px; margin: 30px 0; }
                .contact-item { margin: 12px 0; font-size: 16px; line-height: 1.5; }
                .contact-label { font-weight: 600; display: inline-block; min-width: 120px; }
                .email-footer { padding: 30px; text-align: center; font-size: 14px; border-top: 1px solid #e5e7eb; }
                .signature { margin-top: 30px; padding-top: 20px; border-top: 2px solid #e5e7eb; }
                .save-contact-btn { display: inline-block; background: linear-gradient(135deg, ${organizationColor} 0%, ${organizationColor}dd 100%); color: white; padding: 14px 28px; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px; margin: 20px 0; }
                @media only screen and (max-width: 600px) {
                  .email-header { padding: 30px 20px; }
                  .email-content { padding: 30px 20px; }
                  .contact-box { padding: 20px; }
                  .contact-label { display: block; margin-bottom: 4px; }
                }
              </style>
            </head>
            <body>
              <div class="email-container">
                <div class="email-header">
                  ${organizationLogo 
                    ? `<img src="${organizationLogo}" alt="${organizationName}" class="email-logo" />` 
                    : `<h1 style="color: white; margin: 0; font-size: 28px; font-weight: 600;">${organizationName || 'D2D Plus'}</h1>`
                  }
                </div>
                
                <div class="email-content">
                  <div class="greeting">
                    Hallo ${customer.first_name} ${customer.last_name || ''},
                  </div>
                  
                  <p style="line-height: 1.6; margin: 20px 0;">
                    vielen Dank für Ihr Interesse. Hier sind die Kontaktdaten Ihres Beraters für Rückfragen:
                  </p>
                  
                  <div class="contact-box">
                    ${advisorAvatarUrl ? `
                    <div style="text-align: center; margin-bottom: 16px;">
                      <img src="${advisorAvatarUrl}" alt="${fullName}" style="width: 80px; height: 80px; border-radius: 50%; object-fit: cover; border: 3px solid ${organizationColor};" />
                    </div>
                    ` : ''}
                    ${organizationName ? `<div class="contact-item" style="text-align: ${advisorAvatarUrl ? 'center' : 'left'};"><span style="font-size: 18px; font-weight: 600;">${organizationName}</span></div>` : ''}
                    <div class="contact-item"><span class="contact-label">Berater:</span><span>${fullName}</span></div>
                    ${profileData?.phone ? `<div class="contact-item"><span class="contact-label">Telefon:</span><span>${profileData.phone}</span></div>` : ''}
                  </div>
                  
                  <p style="line-height: 1.6; margin: 20px 0;">
                    Bei Fragen können Sie sich jederzeit gerne bei uns melden. Wir stehen Ihnen gerne zur Verfügung!
                  </p>
                  
                  <div style="text-align: center; margin: 30px 0;">
                    <a href="${vCardDownloadUrl}" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       download="${advisorLastName.replace(/\s+/g, '_')}_Kontakt.vcf"
                       style="display: inline-block; background: linear-gradient(135deg, ${organizationColor} 0%, ${organizationColor}dd 100%); color: white !important; padding: 16px 32px; border-radius: 12px; font-weight: 700; font-size: 18px; text-decoration: none;">
                      Kontakt hinzufügen
                    </a>
                  </div>
                  
                  <div class="signature">
                    <p style="margin: 5px 0;">Mit freundlichen Grüßen,</p>
                    <p style="margin: 5px 0; font-weight: 600; font-size: 16px;">${fullName}</p>
                    ${organizationName ? `<p style="margin: 5px 0;">${organizationName}</p>` : ''}
                  </div>
                </div>
                
                <div class="email-footer">
                  <p style="margin: 0;">Diese E-Mail wurde automatisch generiert.</p>
                </div>
              </div>
            </body>
            </html>
          `,
          text: `
Hallo ${customer.first_name} ${customer.last_name || ''},

vielen Dank für Ihr Interesse. Hier sind die Kontaktdaten Ihres Beraters:

${organizationName || ''}
Berater: ${fullName}
${profileData?.phone ? `Telefon: ${profileData.phone}\n` : ''}

Bei Fragen können Sie sich jederzeit bei uns melden.

📱 Kontaktdaten im Anhang
Öffnen Sie den Anhang dieser E-Mail (${advisorLastName.replace(/\s+/g, '_')}_Kontakt.vcf), um die Kontaktdaten zu Ihrem Adressbuch hinzuzufügen.

Mit freundlichen Grüßen,
${fullName}${organizationName ? `\n${organizationName}` : ''}
          `
        }
      });

      if (emailError) throw emailError;

      // Add note to customer about sending contact info
      try {
        const currentNotes = customer.notes || "";
        const now = new Date();
        const contactNote = `[${format(now, 'dd.MM.yyyy HH:mm')}] 📧 Kontaktdaten an Kunde gesendet (${customer.email})`;
        
        const updatedNotes = currentNotes 
          ? `${contactNote}\n\n${currentNotes}`
          : contactNote;

        const { error: noteError } = await supabase
          .from('customers')
          .update({ notes: updatedNotes })
          .eq('id', customer.id);

        if (noteError) {
          console.error('Error updating customer notes:', noteError);
        } else {
          // Update local state
          setSelectedCustomer(prev => prev ? { ...prev, notes: updatedNotes } : null);
          setCustomers(prev => prev.map(c => 
            c.id === customer.id ? { ...c, notes: updatedNotes } : c
          ));
        }
      } catch (noteError) {
        console.error('Error adding contact info note:', noteError);
      }

      toast({
        title: "E-Mail versendet",
        description: `Ihre Kontaktdaten wurden an ${customer.email} gesendet.`
      });
    } catch (error) {
      console.error('Error sending contact info:', error);
      toast({
        title: "Fehler",
        description: "Die E-Mail konnte nicht versendet werden.",
        variant: "destructive"
      });
    }
  };

  const handleCreateAppointment = async (appointment: {
    title: string;
    date: Date;
    time: string;
    notes?: string;
  }) => {
    if (!selectedCustomer) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Fehler",
          description: "Sie müssen angemeldet sein.",
          variant: "destructive"
        });
        return;
      }

      // Get profile for organization_id
      const { data: profileData } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', session.user.id)
        .single();

      if (!profileData?.organization_id) {
        toast({
          title: "Fehler",
          description: "Keine Organisation gefunden.",
          variant: "destructive"
        });
        return;
      }

      // Find d2d_unit linked to this customer
      const { data: linkedUnit } = await supabase
        .from('d2d_units')
        .select('id')
        .eq('customer_id', selectedCustomer.id)
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (linkedUnit) {
        // Generate appointment token
        const appointmentToken = crypto.randomUUID();

        // Update existing unit with appointment
        const { error } = await supabase
          .from('d2d_units')
          .update({
            next_reminder: appointment.date.toISOString(),
            manual_reminder: true,
            reminder_reason: appointment.title,
            follow_up_notes: appointment.notes || null,
            appointment_token: appointmentToken,
            updated_at: new Date().toISOString()
          })
          .eq('id', linkedUnit.id);

        if (error) throw error;

        // Add appointment note to customer
        try {
          const currentNotes = selectedCustomer.notes || "";
          const now = new Date();
          const appointmentNote = `[${format(now, 'dd.MM.yyyy HH:mm')}] 📅 Termin erstellt: "${appointment.title}" am ${format(appointment.date, 'dd.MM.yyyy')} um ${appointment.time} Uhr`;
          
          const updatedNotes = currentNotes 
            ? `${appointmentNote}\n\n${currentNotes}`
            : appointmentNote;

          const { error: noteError } = await supabase
            .from('customers')
            .update({ notes: updatedNotes })
            .eq('id', selectedCustomer.id);

          if (noteError) {
            console.error('Error updating customer notes:', noteError);
          } else {
            // Update local state
            setSelectedCustomer(prev => prev ? { ...prev, notes: updatedNotes } : null);
            setCustomers(prev => prev.map(c => 
              c.id === selectedCustomer.id ? { ...c, notes: updatedNotes } : c
            ));
          }
        } catch (noteError) {
          console.error('Error adding appointment note:', noteError);
        }

        // Send appointment email to customer
        try {
          const { error: emailError } = await supabase.functions.invoke('send-customer-appointment-email', {
            body: {
              customer_id: selectedCustomer.id,
              appointment_date: appointment.date.toISOString(),
              title: appointment.title,
              notes: appointment.notes,
              user_email: session.user.email,
              appointment_token: appointmentToken
            }
          });

          if (emailError) {
            console.error('Error sending appointment email:', emailError);
            toast({
              title: "Termin erstellt",
              description: `Termin "${appointment.title}" wurde erstellt, aber die E-Mail konnte nicht versendet werden.`,
              variant: "default"
            });
          } else {
            toast({
              title: "Termin erstellt",
              description: `Termin "${appointment.title}" wurde erfolgreich erstellt und per E-Mail versendet.`
            });
          }
        } catch (emailError) {
          console.error('Error sending appointment email:', emailError);
          toast({
            title: "Termin erstellt",
            description: `Termin "${appointment.title}" wurde erstellt, aber die E-Mail konnte nicht versendet werden.`,
            variant: "default"
          });
        }
      } else {
        toast({
          title: "Info",
          description: "Keine verknüpfte Einheit gefunden. Termin kann nur für verknüpfte Einheiten erstellt werden.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast({
        title: "Fehler",
        description: "Der Termin konnte nicht erstellt werden.",
        variant: "destructive"
      });
    }
  };

  const deleteProtocolEntry = (customerId: string, entryId: string) => {
    setCustomers(prev => prev.map(customer => customer.id === customerId ? {
      ...customer,
      protocol: customer.protocol.filter(entry => entry.id !== entryId)
    } : customer));
    if (selectedCustomer && selectedCustomer.id === customerId) {
      setSelectedCustomer(prev => prev ? {
        ...prev,
        protocol: prev.protocol.filter(entry => entry.id !== entryId)
      } : null);
    }
    toast({
      title: "Notiz gelöscht",
      description: "Der Protokoll-Eintrag wurde entfernt."
    });
  };

  // Function to delete a specific note entry
  const deleteNoteEntry = async (customerId: string, noteContent: string) => {
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return;
      const customer = customers.find(c => c.id === customerId);
      if (!customer) return;
      const notes = customer.notes || "";
      const entries = notes.split('\n\n').filter(entry => entry.trim());

      // Find and remove the specific entry by content
      const entryIndex = entries.findIndex(entry => entry.trim() === noteContent.trim());
      if (entryIndex === -1) {
        toast({
          title: "Fehler",
          description: "Notiz nicht gefunden.",
          variant: "destructive"
        });
        return;
      }

      // Store the deleted entry for undo functionality
      const deletedEntry = entries[entryIndex];

      // Remove the specific entry
      entries.splice(entryIndex, 1);

      // Rejoin the remaining entries
      const updatedNotes = entries.join('\n\n');
      const {
        error
      } = await supabase.from('customers').update({
        notes: updatedNotes
      }).eq('id', customerId).eq('user_id', session.user.id);
      if (error) {
        console.error('Error deleting note:', error);
        toast({
          title: "Fehler",
          description: "Notiz konnte nicht gelöscht werden.",
          variant: "destructive"
        });
        return;
      }

      // Update local state
      const updatedCustomer = {
        ...customer,
        notes: updatedNotes
      };
      setCustomers(prev => prev.map(c => c.id === customerId ? updatedCustomer : c));
      if (selectedCustomer && selectedCustomer.id === customerId) {
        setSelectedCustomer(updatedCustomer);
      }

      // Function to undo the deletion
      const undoDeletion = async () => {
        try {
          // Recreate the original notes array
          const originalEntries = notes.split('\n\n').filter(entry => entry.trim());
          const restoredNotes = originalEntries.join('\n\n');
          const {
            error: undoError
          } = await supabase.from('customers').update({
            notes: restoredNotes
          }).eq('id', customerId).eq('user_id', session.user.id);
          if (undoError) {
            console.error('Error undoing deletion:', undoError);
            return;
          }

          // Update local state back to original
          const restoredCustomer = {
            ...customer,
            notes: restoredNotes
          };
          setCustomers(prev => prev.map(c => c.id === customerId ? restoredCustomer : c));
          if (selectedCustomer && selectedCustomer.id === customerId) {
            setSelectedCustomer(restoredCustomer);
          }
          toast({
            title: "Notiz wiederhergestellt",
            description: "Die Notiz wurde erfolgreich wiederhergestellt."
          });
        } catch (error) {
          console.error('Error during undo:', error);
        }
      };
      toast({
        title: "Notiz gelöscht",
        description: <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            <span className="text-xs sm:text-sm">Die Notiz wurde erfolgreich entfernt.</span>
            <button onClick={undoDeletion} className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium self-start">
              Rückgängig machen
            </button>
          </div>
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive"
      });
    }
  };
  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);

    // Extract birth date, customer number, etc. from notes if they exist
    const extractFromNotes = (notes: string | null) => {
      if (!notes) return {
        birthDate: "",
        customerNumber: "",
        servicePin: "",
        iban: ""
      };
      const birthDateMatch = notes.match(/Geburtsdatum: ([^,]+)/);
      const customerNumberMatch = notes.match(/Kundennummer: ([^,]+)/);
      const servicePinMatch = notes.match(/Service-PIN: ([^,]+)/);
      const ibanMatch = notes.match(/IBAN: ([^,]+)/);
      return {
        birthDate: birthDateMatch ? birthDateMatch[1].trim() : "",
        customerNumber: customerNumberMatch ? customerNumberMatch[1].trim() : "",
        servicePin: servicePinMatch ? servicePinMatch[1].trim() : "",
        iban: ibanMatch ? ibanMatch[1].trim() : ""
      };
    };
    const extracted = extractFromNotes(customer.notes);
    setFormData({
      firstName: customer.first_name || "",
      lastName: customer.last_name || "",
      address: customer.address || "",
      birthDate: customer.birthDate || extracted.birthDate,
      customerNumber: customer.customerNumber || extracted.customerNumber,
      servicePin: customer.servicePin || extracted.servicePin,
      iban: customer.iban || extracted.iban,
      email: customer.email || "",
      phone: customer.phone || ""
    });
    setIsEditingCustomer(true);
  };
  const handleUpdateCustomer = async () => {
    if (!formData.firstName || !formData.lastName || !formData.address || !formData.birthDate) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie alle Pflichtfelder aus.",
        variant: "destructive"
      });
      return;
    }
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein, um Kunden zu bearbeiten.",
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }
    try {
      const {
        data,
        error
      } = await supabase.from('customers').update({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        address: formData.address.trim(),
        customer_number: formData.customerNumber.trim() || null,
        service_pin: formData.servicePin.trim() || null,
        iban: formData.iban.trim() || null,
        birth_date: formData.birthDate || null,
        updated_at: new Date().toISOString()
      }).eq('id', selectedCustomer?.id).select().single();
      if (error) {
        console.error('Error updating customer:', error);
        toast({
          title: "Fehler",
          description: `Kunde konnte nicht aktualisiert werden: ${error.message}`,
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      // Update local state
      const updatedCustomer: Customer = {
        id: data.id,
        user_id: data.user_id,
        first_name: data.first_name,
        last_name: data.last_name,
        name: `${data.first_name} ${data.last_name || ""}`.trim(),
        email: data.email,
        phone: data.phone,
        address: data.address,
        notes: data.notes,
        customerNumber: data.customer_number || "",
        servicePin: data.service_pin || "",
        iban: data.iban || "",
        birthDate: data.birth_date || "",
        created_at: data.created_at,
        updated_at: data.updated_at
      };
      setCustomers(prev => prev.map(c => c.id === data.id ? updatedCustomer : c));
      // Keep customer selected and exit edit mode to show details
      setSelectedCustomer(updatedCustomer);
      setIsEditingCustomer(false);
      // Reset address search state
      setSelectedStreet(null);
      setSearchStep('street');
      setAddressSuggestions([]);
      setFormData({
        firstName: "",
        lastName: "",
        address: "",
        birthDate: "",
        customerNumber: "",
        servicePin: "",
        iban: "",
        email: "",
        phone: ""
      });
      toast({
        title: "Kunde aktualisiert",
        description: `${updatedCustomer.name} wurde erfolgreich aktualisiert.`
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCustomerFromSwipe = (customer: Customer) => {
    setCustomerToDelete(customer);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteCustomer = async () => {
    const customerToRemove = customerToDelete || selectedCustomer;
    if (!customerToRemove) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Fehler", 
          description: "Sie müssen angemeldet sein.",
          variant: "destructive"
        });
        return;
      }
      
      // First, get all units linked to this customer and remove WIN tag
      const { data: linkedUnits } = await supabase
        .from('d2d_units')
        .select('id, tags')
        .eq('customer_id', customerToRemove.id)
        .eq('user_id', session.user.id);
      
      if (linkedUnits && linkedUnits.length > 0) {
        // Remove WIN tag and customer_id from all linked units
        for (const unit of linkedUnits) {
          const updatedTags = (unit.tags || []).filter(tag => tag !== 'WIN');
          await supabase
            .from('d2d_units')
            .update({ 
              customer_id: null,
              tags: updatedTags
            })
            .eq('id', unit.id)
            .eq('user_id', session.user.id);
        }
      }
      
      // Then delete the customer
      const { error } = await supabase.from('customers').delete().eq('id', customerToRemove.id).eq('user_id', session.user.id);
      if (error) {
        console.error('Error deleting customer:', error);
        toast({
          title: "Fehler",
          description: `Kunde konnte nicht gelöscht werden: ${error.message}`,
          variant: "destructive"
        });
        return;
      }

      // Remove from local state
      setCustomers(prev => prev.filter(c => c.id !== customerToRemove.id));
      if (selectedCustomer && selectedCustomer.id === customerToRemove.id) {
        setSelectedCustomer(null);
        setIsEditingCustomer(false);
      }
      setCustomerToDelete(null);
      setShowDeleteConfirmation(false);
      toast({
        title: "Kunde gelöscht",
        description: `${customerToRemove.name} wurde erfolgreich gelöscht und WIN-Tag entfernt.`
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive"
      });
    }
  };
  const handleFileUpload = async (files: FileList | null, type: "needs" | "documents") => {
    if (!files || files.length === 0) return;
    
    const filesArray = Array.from(files);
    setTotalFilesToUpload(filesArray.length);
    setCurrentFileUploading(0);
    setUploadProgress(0);
    setIsUploading(true);
    
    try {
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        setCurrentFileUploading(i + 1);
        
        // Calculate base progress for this file (each file gets equal portion of 100%)
        const baseProgress = (i / filesArray.length) * 100;
        const fileProgressStep = 100 / filesArray.length;
        
        // Progress: Starting file processing (25% of this file's portion)
        setUploadProgress(Math.round(baseProgress + fileProgressStep * 0.25));
        
        // Skip compression for small files (< 1MB) to speed up processing
        let processedFile: File | Blob = file;
        if (file.size > 1024 * 1024) {
          // Progress: Compressing file (75% of this file's portion)
          setUploadProgress(Math.round(baseProgress + fileProgressStep * 0.75));
          const compressedFileData = await compressFile(file);
          processedFile = compressedFileData.compressedFile;
        }
        
        // Add processed file to the appropriate array - cast Blob to File if needed
        const finalFile = processedFile as File;
        
        // Add processed file to the appropriate array
        if (type === "needs") {
          setNeedsFiles(prev => [...prev, finalFile]);
        } else {
          setDocumentFiles(prev => [...prev, finalFile]);
        }
        
        // Update progress - completed file (100% of this file's portion)
        const progress = Math.round(((i + 1) / filesArray.length) * 100);
        setUploadProgress(Math.min(progress, 100)); // Ensure never exceeds 100%
      }
      
      toast({
        title: "Erfolg",
        description: `${filesArray.length} Datei(en) erfolgreich komprimiert und hinzugefügt.`
      });
    } catch (error) {
      console.error('Error processing files:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Verarbeiten der Dateien.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentFileUploading(0);
      setTotalFilesToUpload(0);
    }
  };
  const removeFile = (index: number, type: "needs" | "documents") => {
    if (type === "needs") {
      setNeedsFiles(prev => prev.filter((_, i) => i !== index));
    } else {
      setDocumentFiles(prev => prev.filter((_, i) => i !== index));
    }
  };
  const uploadFiles = async () => {
    if (!selectedCustomer || documentFiles.length === 0) return;
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return;
      for (const file of documentFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${session.user.id}/${selectedCustomer.id}/${fileName}`;

        // Upload file to storage
        const {
          error: uploadError
        } = await supabase.storage.from('customer-files').upload(filePath, file);
        if (!uploadError) {
          // Save file info to database
          const {
            data,
            error: dbError
          } = await supabase.from('customer_files').insert({
            customer_id: selectedCustomer.id,
            user_id: session.user.id,
            file_name: file.name,
            file_path: filePath,
            file_type: file.type,
            file_size: file.size
          }).select().single();
          if (!dbError && data) {
            setCustomerFiles(prev => [...prev, data]);
          }
        }
      }
      setDocumentFiles([]);
      toast({
        title: "Dateien hochgeladen",
        description: "Die Dateien wurden erfolgreich hochgeladen."
      });
    } catch (error) {
      console.error('Error uploading files:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Hochladen der Dateien.",
        variant: "destructive"
      });
    }
  };
  const deleteFile = async (fileId: string) => {
    try {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return;

      // Get file info first
      const {
        data: fileData
      } = await supabase.from('customer_files').select('file_path').eq('id', fileId).single();
      if (fileData) {
        // Delete from storage
        await supabase.storage.from('customer-files').remove([fileData.file_path]);

        // Delete from database
        const {
          error
        } = await supabase.from('customer_files').delete().eq('id', fileId).eq('user_id', session.user.id);
        if (!error) {
          setCustomerFiles(prev => prev.filter(f => f.id !== fileId));
          toast({
            title: "Datei gelöscht",
            description: "Die Datei wurde erfolgreich gelöscht."
          });
        }
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Löschen der Datei.",
        variant: "destructive"
      });
    }
  };
  const previewDocument = async (file: any, index: number) => {
    try {
      const {
        data
      } = await supabase.storage.from('customer-files').createSignedUrl(file.file_path, 60);
      if (data?.signedUrl) {
        setPreviewFile({
          name: file.file_name,
          url: data.signedUrl,
          type: file.file_type
        });
        setCurrentFileIndex(index);
      }
    } catch (error) {
      console.error('Error creating preview:', error);
      toast({
        title: "Fehler",
        description: "Datei konnte nicht angezeigt werden.",
        variant: "destructive"
      });
    }
  };


  // Filter customers based on search query
  const filteredCustomers = customers.filter(customer => {
    const searchTerm = searchQuery.toLowerCase();
    return customer.name.toLowerCase().includes(searchTerm) || customer.email && customer.email.toLowerCase().includes(searchTerm) || customer.phone && customer.phone.includes(searchTerm) || customer.address && customer.address.toLowerCase().includes(searchTerm) || customer.customerNumber && customer.customerNumber.toLowerCase().includes(searchTerm);
  });
  if (loading) {
    return <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Lade Kunden...</p>
          </div>
        </div>
      </div>;
  }

  // Show customer creation dialog
  if (isCreating) {
    return <div className="container mx-auto px-4 py-4 md:py-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="sm" onClick={() => setIsCreating(false)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Zurück
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">Neuen Kunden erstellen</h1>
              <p className="text-sm text-muted-foreground">Neuen Kunden anlegen</p>
            </div>
          </div>

          <Card>
            <CardHeader className="px-4 md:px-6">
              <CardTitle className="text-lg md:text-xl">Kundendaten</CardTitle>
              <CardDescription className="text-sm">
                Bitte füllen Sie alle erforderlichen Felder aus. Felder mit * sind Pflichtfelder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6 px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">

                <div className="space-y-2">
                  <Label htmlFor="firstName">Vorname *</Label>
                  <Input id="firstName" value={formData.firstName} onChange={e => setFormData(prev => ({
                  ...prev,
                  firstName: e.target.value
                }))} placeholder="Max" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Nachname *</Label>
                  <Input id="lastName" value={formData.lastName} onChange={e => setFormData(prev => ({
                  ...prev,
                  lastName: e.target.value
                }))} placeholder="Mustermann" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input id="email" type="email" value={formData.email} onChange={e => setFormData(prev => ({
                  ...prev,
                  email: e.target.value
                }))} placeholder="max@beispiel.de" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input id="phone" value={formData.phone} onChange={e => setFormData(prev => ({
                  ...prev,
                  phone: e.target.value
                }))} placeholder="+49 123 456789" />
                </div>

                <div className="space-y-2 relative md:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="address">Adresse *</Label>
                    {selectedStreet && searchStep === 'houseNumber' && <Button type="button" variant="ghost" size="sm" onClick={resetAddressSearch} className="text-xs">
                        Neue Straße wählen
                      </Button>}
                  </div>
                  <Input id="address" ref={addressInputRef} value={formData.address} onChange={e => setFormData(prev => ({
                  ...prev,
                  address: e.target.value
                }))} placeholder={searchStep === 'street' ? "Straßenname eingeben..." : "Hausnummer eingeben oder aus Liste wählen"} />
                  {isLoadingAddress && <div className="absolute right-3 top-8 text-sm text-muted-foreground">
                      {searchStep === 'street' ? 'Suche Straßen...' : 'Suche Hausnummern...'}
                    </div>}
                </div>

                {/* Address Suggestions */}
                {addressSuggestions.length > 0 && <div className="md:col-span-2 space-y-2">
                    <Label>
                      {searchStep === 'street' ? 'Straßen auswählen:' : selectedStreet ? `Hausnummern für ${selectedStreet.street}:` : 'Adressvorschläge:'}
                    </Label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                      {addressSuggestions.map((suggestion, index) => <div key={index} onClick={() => selectAddressSuggestion(suggestion)} className="p-3 hover:bg-muted rounded-md cursor-pointer text-sm border">
                          {suggestion.isStreet ? <>
                              <div className="font-medium">{suggestion.street}</div>
                              <div className="text-muted-foreground text-xs">
                                {suggestion.postcode} {suggestion.city}, {suggestion.state}
                              </div>
                            </> : suggestion.isHouseNumber ? <>
                              <div className="font-medium">
                                {suggestion.displayName === "Ohne Hausnummer fortfahren" ? suggestion.displayName : `${selectedStreet?.street} ${suggestion.displayName}`}
                              </div>
                              <div className="text-muted-foreground text-xs">
                                {suggestion.fullAddress}
                              </div>
                            </> : <>
                              <div className="font-medium">{suggestion.fullAddress}</div>
                              <div className="text-muted-foreground text-xs">
                                {suggestion.displayName}
                              </div>
                            </>}
                        </div>)}
                    </div>
                  </div>}

                <div className="space-y-2">
                  <Label htmlFor="birthDate">Geburtsdatum *</Label>
                  <Input id="birthDate" type="date" value={formData.birthDate} onChange={e => setFormData(prev => ({
                  ...prev,
                  birthDate: e.target.value
                }))} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customerNumber">Kundennummer</Label>
                  <Input id="customerNumber" value={formData.customerNumber} onChange={e => setFormData(prev => ({
                  ...prev,
                  customerNumber: e.target.value
                }))} placeholder="12345678" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="servicePin">Service-PIN</Label>
                  <Input id="servicePin" value={formData.servicePin} onChange={e => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setFormData(prev => ({
                    ...prev,
                    servicePin: value
                  }));
                }} placeholder="123456" maxLength={6} pattern="[0-9]{6}" />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="iban">IBAN</Label>
                  <Input id="iban" value={formData.iban} onChange={e => handleIbanChange(e.target.value)} placeholder="DE89 3704 0044 0532 0130 00" maxLength={34} className={ibanError ? "border-red-500" : ibanSuccess ? "border-green-500" : ""} />
                  {ibanError && <p className="text-sm text-red-500">{ibanError}</p>}
                  {ibanSuccess && <p className="text-sm text-green-600">{ibanSuccess}</p>}
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <div>
                  <Label>Dokumente</Label>
                  
                  {/* Upload Progress */}
                  {isUploading && (
                    <div className="mb-4 p-4 bg-muted rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">
                          Datei {currentFileUploading} von {totalFilesToUpload} wird hochgeladen...
                        </span>
                        <span className="text-sm text-muted-foreground">{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} className="w-full" />
                    </div>
                  )}

                  {/* Upload Area */}
                  <div className={`mt-2 border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    <input 
                      type="file" 
                      multiple 
                      accept="image/*,.pdf,.doc,.docx,.txt,.rtf,.odt,.xls,.xlsx" 
                      onChange={e => handleFileUpload(e.target.files, "documents")} 
                      className="hidden" 
                      id="documents-upload"
                      disabled={isUploading}
                    />
                    <Label htmlFor="documents-upload" className="cursor-pointer">
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <span>{isUploading ? 'Upload läuft...' : 'Dateien auswählen'}</span>
                          <p className="text-xs text-muted-foreground mt-2">
                            Bilder, PDFs und Dokumente
                          </p>
                        </div>
                      </div>
                    </Label>
                  </div>
                  
                  {documentFiles.length > 0 && <div className="mt-4 text-left">
                      <p className="text-sm text-muted-foreground mb-3">
                        {documentFiles.length} Datei(en) ausgewählt
                      </p>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {documentFiles.map((file, index) => {
                          const isImage = file.type.startsWith('image/');
                          const previewUrl = isImage ? URL.createObjectURL(file) : null;
                          
                          return (
                            <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded text-sm">
                              {isImage && previewUrl ? (
                                <img 
                                  src={previewUrl} 
                                  alt={file.name}
                                  className="h-8 w-8 object-cover rounded border"
                                  onLoad={() => URL.revokeObjectURL(previewUrl)}
                                />
                              ) : (
                                <File className="h-4 w-4" />
                              )}
                              <span className="flex-1 truncate">{file.name}</span>
                              <Button size="sm" variant="ghost" onClick={() => removeFile(index, "documents")} className="h-6 w-6 p-0">
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>}
                </div>
              </div>

              <Separator />

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                <Button variant="outline" onClick={() => setIsCreating(false)} className="order-2 sm:order-1">
                  Abbrechen
                </Button>
                <Button onClick={handleCreateCustomer} disabled={isSubmitting} className="gap-2 order-1 sm:order-2">
                  <Save className="h-4 w-4" />
                  {isSubmitting ? "Wird erstellt..." : "Kunde erstellen"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>;
  }

  // Show customer edit dialog
  if (isEditingCustomer && selectedCustomer) {
    return (
      <div className="fixed inset-0 bg-background z-50 overflow-y-auto">
        <div className="container mx-auto px-4 py-4 md:py-8 min-h-full">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <Button variant="ghost" size="sm" onClick={() => {
                setIsEditingCustomer(false);
              }} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">Kunde bearbeiten</h1>
                <p className="text-sm text-muted-foreground">
                  Bearbeiten Sie die Daten von {selectedCustomer.name}
                </p>
              </div>
            </div>

          <Card>
            <CardHeader className="px-4 md:px-6">
              <CardTitle className="text-lg md:text-xl">Kundendaten</CardTitle>
              <CardDescription className="text-sm">
                Bitte füllen Sie alle erforderlichen Felder aus. Felder mit * sind Pflichtfelder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6 px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">

                <div className="space-y-2">
                  <Label htmlFor="firstName">Vorname *</Label>
                  <Input id="firstName" value={formData.firstName} onChange={e => setFormData(prev => ({
                  ...prev,
                  firstName: e.target.value
                }))} placeholder="Max" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Nachname *</Label>
                  <Input id="lastName" value={formData.lastName} onChange={e => setFormData(prev => ({
                  ...prev,
                  lastName: e.target.value
                }))} placeholder="Mustermann" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input id="email" type="email" value={formData.email} onChange={e => setFormData(prev => ({
                  ...prev,
                  email: e.target.value
                }))} placeholder="max@beispiel.de" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input id="phone" value={formData.phone} onChange={e => setFormData(prev => ({
                  ...prev,
                  phone: e.target.value
                }))} placeholder="+49 123 456789" />
                </div>

                <div className="space-y-2 relative md:col-span-2">
                  <Label htmlFor="address">Adresse *</Label>
                  <Input id="address" value={formData.address} onChange={e => setFormData(prev => ({
                  ...prev,
                  address: e.target.value
                }))} placeholder="Musterstraße 123, 12345 Musterstadt" />
                  {isLoadingAddress && <div className="absolute right-3 top-8 text-sm text-muted-foreground">
                      Suche...
                    </div>}
                </div>

                {/* Address Suggestions */}
                {addressSuggestions.length > 0 && <div className="md:col-span-2 space-y-2">
                    <Label>Adressvorschläge:</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                      {addressSuggestions.map((suggestion, index) => <div key={index} onClick={() => selectAddressSuggestion(suggestion)} className="p-3 hover:bg-muted rounded-md cursor-pointer text-sm border">
                          <div className="font-medium">{suggestion.fullAddress}</div>
                          <div className="text-muted-foreground text-xs">
                            {suggestion.displayName}
                          </div>
                        </div>)}
                    </div>
                  </div>}

                <div className="space-y-2">
                  <Label htmlFor="birthDate">Geburtsdatum *</Label>
                  <Input id="birthDate" type="date" value={formData.birthDate} onChange={e => setFormData(prev => ({
                  ...prev,
                  birthDate: e.target.value
                }))} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customerNumber">Kundennummer</Label>
                  <Input id="customerNumber" value={formData.customerNumber} onChange={e => setFormData(prev => ({
                  ...prev,
                  customerNumber: e.target.value
                }))} placeholder="12345678" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="servicePin">Service-PIN</Label>
                  <Input id="servicePin" value={formData.servicePin} onChange={e => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setFormData(prev => ({
                    ...prev,
                    servicePin: value
                  }));
                }} placeholder="123456" maxLength={6} pattern="[0-9]{6}" />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="iban">IBAN</Label>
                  <Input id="iban" value={formData.iban} onChange={e => handleIbanChange(e.target.value)} placeholder="DE89 3704 0044 0532 0130 00" maxLength={34} className={ibanError ? "border-red-500" : ibanSuccess ? "border-green-500" : ""} />
                  {ibanError && <p className="text-sm text-red-500">{ibanError}</p>}
                  {ibanSuccess && <p className="text-sm text-green-600">{ibanSuccess}</p>}
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                {/* Delete Button - Separate row on mobile, left side on desktop */}
                <Button variant="destructive" onClick={() => {
                console.log('Delete button clicked, showing confirmation');
                setShowDeleteConfirmation(true);
              }} className="gap-2 w-full sm:w-auto order-2 sm:order-1">
                  <Trash2 className="h-4 w-4" />
                  Kunde löschen
                </Button>
                
                {/* Action Buttons - Top row on mobile, right side on desktop */}
                <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 order-1 sm:order-2">
                  <Button variant="outline" onClick={() => {
                  setIsEditingCustomer(false);
                  setSelectedCustomer(null); // Zurück zur Kundenliste
                }} className="w-full sm:w-auto">
                    Abbrechen
                  </Button>
                  <Button onClick={handleUpdateCustomer} disabled={isSubmitting} className="gap-2 w-full sm:w-auto">
                    <Save className="h-4 w-4" />
                    {isSubmitting ? "Wird gespeichert..." : "Änderungen speichern"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delete Confirmation Dialog */}
          <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Kunde löschen</DialogTitle>
                <DialogDescription>
                  Sind Sie sicher, dass Sie {(customerToDelete || selectedCustomer)?.name} löschen möchten?
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 pt-4">
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowDeleteConfirmation(false)} className="flex-1">
                    Abbrechen
                  </Button>
                  <Button variant="destructive" onClick={handleDeleteCustomer} className="flex-1 gap-2">
                    <Trash2 className="h-4 w-4" />
                    Endgültig löschen
                  </Button>
                </div>
              </div>
            </DialogContent>
           </Dialog>
        </div>
      </div>
    </div>
    );
  }
  
  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-screen max-w-full bg-gradient-subtle">
        {/* Sidebar - Hidden when used as modal */}
        {!onClose && <AppSidebar />}
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top Bar */}
          <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
            {!onClose && <SidebarTrigger className="ml-2" />}
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="ml-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück
              </Button>
            )}
            <div className="flex-1 flex items-center justify-center px-4">
              <div>
                <h1 className="text-lg font-bold">Kundenverwaltung</h1>
              </div>
              <div className="flex items-center gap-4">
              </div>
            </div>
          </header>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden min-w-0">
            <div className="w-full h-full px-2 sm:px-4 py-4 md:py-8 overflow-y-auto">
              <div className="w-full min-w-0 max-w-none">

        {/* Search and Filter */}
        {customers.length > 0 && <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Suchen nach Name, Adresse, PLZ oder Kundennummer..." 
                      value={searchQuery} 
                      onChange={e => setSearchQuery(e.target.value)} 
                      className="pl-10 pr-10" 
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Suchtext löschen"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                <Button onClick={() => setIsCreating(true)} className="gap-2 whitespace-nowrap">
                  <Plus className="h-4 w-4" />
                  Kunde Anlegen
                </Button>
              </div>
            </CardContent>
          </Card>}

        {/* Customers List */}
        {filteredCustomers.length > 0 ? <div className="grid gap-4 w-full">
            {filteredCustomers.map(customer => 
              <SwipeableCustomerCard 
                key={customer.id}
                customer={customer}
                onSelect={() => {
                  console.log('Selected customer data:', customer);
                  setSelectedCustomer(customer);
                }}
                onEdit={() => handleEditCustomer(customer)}
                onDelete={() => handleDeleteCustomerFromSwipe(customer)}
              />
            )}
          </div> : customers.length > 0 ? <Card>
            <CardContent className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Kunden gefunden</h3>
              <p className="text-muted-foreground">
                Ihre Suche hat keine Ergebnisse geliefert. Versuchen Sie andere Suchbegriffe.
              </p>
            </CardContent>
          </Card> : <Card>
            <CardContent className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Noch keine Kunden</h3>
              <p className="text-muted-foreground mb-4">
                Erstellen Sie Ihren ersten Kunden, um zu beginnen.
              </p>
              <Button onClick={() => setIsCreating(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Ersten Kunden erstellen
              </Button>
            </CardContent>
          </Card>}

        {/* Customer Details Modal */}
        {selectedCustomer && !isEditingCustomer && <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="relative">
                <div className="absolute top-2 right-2 z-10">
                  <button className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors" onClick={() => {
                        if (onClose) {
                          onClose();
                        } else {
                          setSelectedCustomer(null);
                          navigate('/customers', {
                            replace: true
                          });
                        }
                      }}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {selectedCustomer.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Customer Info */}
                <div className="relative">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2">Kontaktinformationen</h4>
                      <div className="text-sm space-y-1">
                        <p><strong>Name:</strong> 
                          <span className="cursor-pointer text-blue-600 hover:text-blue-800 underline ml-1" onClick={() => {
                              const firstName = selectedCustomer.name.split(' ')[0];
                              navigator.clipboard.writeText(firstName);
                              toast({
                                title: "Vorname kopiert",
                                description: "Vorname wurde in die Zwischenablage kopiert."
                              });
                            }}>
                            {selectedCustomer.name.split(' ')[0]}
                          </span>
                          {selectedCustomer.name.split(' ').length > 1 && (
                            <>
                              {" "}
                              <span className="cursor-pointer text-blue-600 hover:text-blue-800 underline" onClick={() => {
                                  const lastName = selectedCustomer.name.split(' ').slice(1).join(' ');
                                  navigator.clipboard.writeText(lastName);
                                  toast({
                                    title: "Nachname kopiert", 
                                    description: "Nachname wurde in die Zwischenablage kopiert."
                                  });
                                }}>
                                {selectedCustomer.name.split(' ').slice(1).join(' ')}
                              </span>
                            </>
                          )}
                        </p>
                        {selectedCustomer.email && <p>
                            <strong>E-Mail:</strong>{" "}
                            <span className="cursor-pointer text-blue-600 hover:text-blue-800 underline" onClick={() => {
                                navigator.clipboard.writeText(selectedCustomer.email || "");
                                toast({
                                  title: "E-Mail kopiert",
                                  description: "E-Mail-Adresse wurde in die Zwischenablage kopiert."
                                });
                              }}>
                              {selectedCustomer.email}
                            </span>
                          </p>}
                        {selectedCustomer.phone && <p>
                            <strong>Telefon:</strong>{" "}
                            <a href={`tel:${selectedCustomer.phone}`} className="cursor-pointer text-blue-600 hover:text-blue-800 underline">
                              {selectedCustomer.phone}
                            </a>
                          </p>}
                        {selectedCustomer.address && <div>
                            <strong>Adresse:</strong>
                            {(() => {
                                const parsedAddress = parseAddress(selectedCustomer.address);
                                 if (parsedAddress?.hasStructuredData) {
                                   const handleCopyText = (text: string, event: React.MouseEvent) => {
                                     event.stopPropagation();
                                     navigator.clipboard.writeText(text);
                                     toast({ title: "Kopiert", description: `"${text}" wurde in die Zwischenablage kopiert` });
                                   };

                                    return <div className="mt-1 flex items-stretch gap-3">
                                      <div className="flex-1 space-y-1 text-sm min-w-0">
                                        {parsedAddress.street && (
                                          <div className="break-words">
                                            <span 
                                              className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                                              onClick={(e) => handleCopyText(parsedAddress.street, e)}
                                              title="Straße kopieren"
                                            >
                                              {parsedAddress.street}
                                            </span>
                                            {parsedAddress.houseNumber && (
                                              <>
                                                {" "}
                                                <span 
                                                  className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                                                  onClick={(e) => handleCopyText(parsedAddress.houseNumber, e)}
                                                  title="Hausnummer kopieren"
                                                >
                                                  {parsedAddress.houseNumber}
                                                </span>
                                              </>
                                            )}
                                          </div>
                                        )}
                                        {(parsedAddress.postcode || parsedAddress.city) && (
                                          <div className="break-words">
                                            {parsedAddress.postcode && (
                                              <span 
                                                className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                                                onClick={(e) => handleCopyText(parsedAddress.postcode, e)}
                                                title="PLZ kopieren"
                                              >
                                                {parsedAddress.postcode}
                                              </span>
                                            )}
                                            {parsedAddress.postcode && parsedAddress.city && " "}
                                            {parsedAddress.city && (
                                              <span 
                                                className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                                                onClick={(e) => handleCopyText(parsedAddress.city, e)}
                                                title="Ort kopieren"
                                              >
                                                {parsedAddress.city}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                        {parsedAddress.state && (
                                          <div className="break-words">
                                            <span 
                                              className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                                              onClick={(e) => handleCopyText(parsedAddress.state, e)}
                                              title="Bundesland kopieren"
                                            >
                                              {parsedAddress.state}
                                            </span>
                                          </div>
                                        )}
                                        {parsedAddress.country && (
                                          <div className="break-words">
                                            <span 
                                              className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
                                              onClick={(e) => handleCopyText(parsedAddress.country, e)}
                                              title="Land kopieren"
                                            >
                                              {parsedAddress.country}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <button 
                                        className="flex-shrink-0 flex flex-col items-center justify-center px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors w-20"
                                        onClick={() => openNavigation(selectedCustomer.address!)}
                                        title="In Navigation öffnen"
                                      >
                                        <Map className="w-6 h-6 mb-1" />
                                        <span className="text-xs text-center">Navigation</span>
                                      </button>
                                    </div>;
                                } else {
                                  return <div className="mt-1 p-2 rounded cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200 hover:border-gray-300 flex" onClick={() => openNavigation(selectedCustomer.address!)} title="In Navigation öffnen">
                                    <div className="w-[70%] flex-shrink-0">
                                      <span className="text-blue-600 hover:text-blue-800">
                                        {selectedCustomer.address}
                                      </span>
                                    </div>
                                    <div className="flex-1 flex justify-end items-center px-2">
                                      <span className="text-sm text-muted-foreground">Navigation →</span>
                                    </div>
                                   </div>;
                                 }
                               })()}
                          </div>}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Zusätzliche Informationen</h4>
                       <div className="text-sm space-y-1">
                         {selectedCustomer.birthDate && <p>
                             <strong>Geburtsdatum:</strong>{" "}
                             <span className="cursor-pointer text-blue-600 hover:text-blue-800 underline" onClick={() => {
                                const formattedDate = new Date(selectedCustomer.birthDate!).toLocaleDateString("de-DE", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric"
                                });
                                navigator.clipboard.writeText(formattedDate);
                                toast({
                                  title: "Geburtsdatum kopiert",
                                  description: "Geburtsdatum wurde in die Zwischenablage kopiert."
                                });
                              }}>
                               {new Date(selectedCustomer.birthDate).toLocaleDateString("de-DE", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric"
                                })}
                             </span>
                           </p>}
                        {selectedCustomer.customerNumber && <p>
                            <strong>Kundennummer:</strong>{" "}
                            <span className="cursor-pointer text-blue-600 hover:text-blue-800 underline" onClick={() => {
                                navigator.clipboard.writeText(selectedCustomer.customerNumber || "");
                                toast({
                                  title: "Kundennummer kopiert",
                                  description: "Kundennummer wurde in die Zwischenablage kopiert."
                                });
                              }}>
                              {selectedCustomer.customerNumber}
                            </span>
                          </p>}
                        {selectedCustomer.servicePin && <p>
                            <strong>Service-PIN:</strong>{" "}
                            <span className="cursor-pointer text-blue-600 hover:text-blue-800 underline" onClick={() => {
                                navigator.clipboard.writeText(selectedCustomer.servicePin || "");
                                toast({
                                  title: "Service-PIN kopiert",
                                  description: "Service-PIN wurde in die Zwischenablage kopiert."
                                });
                              }}>
                              {selectedCustomer.servicePin}
                            </span>
                          </p>}
                        {selectedCustomer.iban && <p>
                            <strong>IBAN:</strong>{" "}
                            <span className="cursor-pointer text-blue-600 hover:text-blue-800 underline" onClick={() => {
                                navigator.clipboard.writeText(selectedCustomer.iban || "");
                                toast({
                                  title: "IBAN kopiert",
                                  description: "IBAN wurde in die Zwischenablage kopiert."
                                });
                              }}>
                              {selectedCustomer.iban}
                            </span>
                          </p>}
                      </div>
                    </div>
                  </div>
                  
                  {/* Send Contact Info and Create Appointment Buttons */}
                  <div className="mt-4 flex justify-center gap-2 flex-wrap">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleSendContactInfo(selectedCustomer)}
                      className="gap-2"
                      disabled={!selectedCustomer.email}
                    >
                      <Mail className="h-4 w-4" />
                      Kontaktdaten an Kunde senden
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowAppointmentDialog(true)}
                      className="gap-2"
                    >
                      <Calendar className="h-4 w-4" />
                      Termin erstellen
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowPerformanceDialog(true)}
                      className="gap-2"
                    >
                      <Trophy className="h-4 w-4" />
                      Leistung erfassen
                    </Button>
                  </div>
                  
                  {/* Performance Dialog */}
                  <WinPerformanceDialog
                    open={showPerformanceDialog}
                    onOpenChange={setShowPerformanceDialog}
                    onConfirm={(metricNames, performanceNotes) => {
                      // Add note with metric names to customer
                      if (selectedCustomer) {
                        const now = new Date();
                        const timestamp = now.toLocaleString('de-DE');
                        const metricsText = metricNames.join(', ');
                        const newNote = `[${timestamp}] 🏆 Leistung erfasst: ${metricsText}${performanceNotes ? ` - ${performanceNotes}` : ''}`;
                        const existingNotes = selectedCustomer.notes || "";
                        const updatedNotes = existingNotes ? `${existingNotes}\n\n${newNote}` : newNote;
                        
                        supabase
                          .from('customers')
                          .update({ notes: updatedNotes })
                          .eq('id', selectedCustomer.id)
                          .then(() => {
                            setSelectedCustomer({
                              ...selectedCustomer,
                              notes: updatedNotes
                            });
                          });
                      }
                    }}
                  />
                  
                  {/* Edit Icon - positioned after customer info */}
                  <Button variant="outline" size="sm" onClick={() => handleEditCustomer(selectedCustomer)} className="absolute top-0 right-0 p-2" title="Kunde bearbeiten">
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>

                <Separator />

                {/* Todo Management */}
                <Collapsible open={isTasksExpanded} onOpenChange={setIsTasksExpanded}>
                  <CollapsibleTrigger asChild>
                     <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                       <div className="flex items-center gap-2">
                         <h4 className="font-semibold text-left">Aufgaben Verwalten</h4>
                          <Badge variant="outline" className="text-xs bg-red-500/20 backdrop-blur-sm border-red-500/30 text-red-600">
                            {openTasksCount}
                          </Badge>
                       </div>
                        {isTasksExpanded ? (
                          <ChevronUp className="h-4 w-4 text-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-foreground" />
                        )}
                     </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4">
                     <TodoManagement customers={[{
                           id: selectedCustomer.id,
                           name: selectedCustomer.name
                         }]} preselectedCustomer={{
                           id: selectedCustomer.id,
                           name: selectedCustomer.name
                         }} onTaskCountChange={setOpenTasksCount} />
                  </CollapsibleContent>
                </Collapsible>

                <Separator />

                {/* Protocol Section */}
                <div>
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Protokoll/Notizen
                  </h4>
                  
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input placeholder="Neue Notiz hinzufügen..." value={protocolNote} onChange={e => setProtocolNote(e.target.value)} onKeyPress={e => e.key === 'Enter' && addProtocolEntry(selectedCustomer.id)} />
                      <Button size="sm" onClick={() => addProtocolEntry(selectedCustomer.id)} disabled={!protocolNote.trim()}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Parse notes for protocol display */}
                    {(() => {
                          const notes = selectedCustomer.notes || "";
                          if (!notes.trim()) {
                            return <p className="text-sm text-muted-foreground">Noch keine Notizen vorhanden.</p>;
                          }

                          // Split notes by double newlines to get individual entries
                          const entries = notes.split('\n\n').filter(entry => entry.trim());
                          const reversedEntries = [...entries].reverse(); // Create a copy and reverse it

                          return <div className="space-y-2 max-h-48 overflow-y-auto">
                          {reversedEntries.map((entry, displayIndex) => {
                              // Try to parse datetime from beginning of entry
                              const match = entry.match(/^(\d{1,2}\.\d{1,2}\.\d{4}, \d{1,2}:\d{2}:\d{2}): (.+)$/s);
                              if (match) {
                                const [, datetime, noteText] = match;
                                // Format the datetime to DD.MM.YYYY format
                                const formattedDate = datetime.replace(/(\d{1,2})\.(\d{1,2})\.(\d{4}), (\d{1,2}):(\d{2}):(\d{2})/, (_, day, month, year, hour, minute, second) => {
                                  const paddedDay = day.padStart(2, '0');
                                  const paddedMonth = month.padStart(2, '0');
                                  const paddedHour = hour.padStart(2, '0');
                                  const paddedMinute = minute.padStart(2, '0');
                                  const paddedSecond = second.padStart(2, '0');
                                  return `${paddedDay}.${paddedMonth}.${year}, ${paddedHour}:${paddedMinute}:${paddedSecond}`;
                                });
                                return <div key={displayIndex} className="flex items-start justify-between bg-muted/50 p-3 rounded-md">
                                  <div className="flex-1">
                                    <p className="text-sm">{noteText}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {formattedDate}
                                    </p>
                                  </div>
                                  <Button variant="ghost" size="sm" onClick={() => deleteNoteEntry(selectedCustomer.id, entry)} className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground ml-2">
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>;
                              } else {
                                // Handle entries without timestamp or different format
                                return <div key={displayIndex} className="flex items-start justify-between bg-muted/50 p-3 rounded-md">
                                  <div className="flex-1">
                                    <p className="text-sm">{entry}</p>
                                  </div>
                                  <Button variant="ghost" size="sm" onClick={() => deleteNoteEntry(selectedCustomer.id, entry)} className="h-8 w-8 p-0 hover:bg-destructive hover:text-destructive-foreground ml-2">
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>;
                              }
                            })}
                        </div>;
                        })()}
                  </div>
                </div>

                <Separator />

                {/* Documents Section */}
                <div>
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Hochgeladene Dokumente
                  </h4>

                  {/* Upload Progress */}
                  {isUploading && (
                    <div className="mb-4 p-4 bg-muted rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">
                          Datei {currentFileUploading} von {totalFilesToUpload} wird hochgeladen...
                        </span>
                        <span className="text-sm text-muted-foreground">{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} className="w-full" />
                    </div>
                  )}

                  {/* Upload Area */}
                  <div className={`mb-6 border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    <input 
                      type="file" 
                      multiple 
                      accept="image/*,.pdf,.doc,.docx,.txt,.rtf,.odt,.xls,.xlsx" 
                      onChange={e => handleDirectFileUpload(e.target.files, selectedCustomer.id)} 
                      className="hidden" 
                      id={`customer-upload-${selectedCustomer.id}`}
                      disabled={isUploading}
                    />
                    <Label htmlFor={`customer-upload-${selectedCustomer.id}`} className="cursor-pointer">
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <span>{isUploading ? 'Upload läuft...' : 'Neue Dateien hochladen'}</span>
                          <p className="text-xs text-muted-foreground mt-2">
                            Bilder, PDFs und Dokumente
                          </p>
                        </div>
                      </div>
                    </Label>
                  </div>
                  
                  {customerFiles.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {customerFiles.map((file, index) => {
                          const getFileIcon = (fileType: string) => {
                            const ext = fileType.toLowerCase();
                            if (ext.includes('image')) {
                              return <Image className="w-8 h-8 text-blue-500" />;
                            }
                            switch (true) {
                              case ext.includes('pdf'):
                                return <FileText className="w-8 h-8 text-red-500" />;
                              case ext.includes('doc') || ext.includes('word'):
                                return <FileText className="w-8 h-8 text-blue-600" />;
                              case ext.includes('xls') || ext.includes('excel'):
                                return <FileText className="w-8 h-8 text-green-600" />;
                              case ext.includes('txt') || ext.includes('text'):
                                return <FileText className="w-8 h-8 text-blue-500" />;
                              default:
                                return <File className="w-8 h-8 text-gray-500" />;
                            }
                          };
                          const previewFileFunction = async (file: any, index: number) => {
                            try {
                              const {
                                data,
                                error
                              } = await supabase.storage.from('customer-files').download(file.file_path);
                              if (error) {
                                console.error('Error loading file:', error);
                                toast({
                                  title: "Fehler",
                                  description: "Datei konnte nicht geladen werden.",
                                  variant: "destructive"
                                });
                                return;
                              }

                              // Create preview URL
                              const url = URL.createObjectURL(data);
                              setPreviewFile({
                                name: file.file_name,
                                url: url,
                                type: file.file_type
                              });
                              setCurrentFileIndex(index);
                            } catch (error) {
                              console.error('Error:', error);
                            }
                          };

                          // Component for file thumbnail
                          const FileThumbnail = ({
                            file
                          }: {
                            file: any;
                          }) => {
                            const [thumbnailUrl, setThumbnailUrl] = useState<string>('');
                            useEffect(() => {
                              if (file.file_type.startsWith('image/')) {
                                supabase.storage.from('customer-files').download(file.file_path).then(({
                                  data,
                                  error
                                }) => {
                                  if (!error && data) {
                                    setThumbnailUrl(URL.createObjectURL(data));
                                  }
                                });
                              }
                              return () => {
                                if (thumbnailUrl) {
                                  URL.revokeObjectURL(thumbnailUrl);
                                }
                              };
                            }, [file]);
                            if (thumbnailUrl) {
                              return <img src={thumbnailUrl} alt={file.file_name} className="w-full h-full object-cover" onError={() => setThumbnailUrl('')} />;
                            }
                            return <div className="w-full h-full flex items-center justify-center bg-muted">
                              <Image className="w-6 h-6 text-muted-foreground" />
                            </div>;
                          };

                          // Component for PDF thumbnail preview
                          const PDFThumbnail = ({
                            file
                          }: {
                            file: any;
                          }) => {
                            const [pdfUrl, setPdfUrl] = useState<string>('');
                            useEffect(() => {
                              supabase.storage.from('customer-files').createSignedUrl(file.file_path, 3600).then(({
                                data,
                                error
                              }) => {
                                if (!error && data?.signedUrl) {
                                  setPdfUrl(data.signedUrl);
                                }
                              });
                            }, [file.file_path]);
                            
                            if (pdfUrl) {
                              return <div className="w-full h-full overflow-hidden rounded">
                                <iframe 
                                  src={pdfUrl} 
                                  className="w-full h-full border-0 pointer-events-none" 
                                  style={{
                                    transform: 'scale(0.3)',
                                    transformOrigin: 'top left',
                                    width: '333%',
                                    height: '333%'
                                  }}
                                />
                              </div>;
                            }
                            
                            return <div className="w-full h-full flex items-center justify-center bg-muted">
                              <FileText className="w-6 h-6 text-red-600" />
                            </div>;
                          };
                          return <Card key={file.id} className="hover:shadow-md transition-shadow relative group">
                            <CardContent className="p-4" onClick={() => previewFileFunction(file, index)} style={{
                              cursor: 'pointer'
                            }}>
                              <div className="flex items-center gap-3">
                                {/* Thumbnail or Icon */}
                                <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-muted">
                                  {file.file_type.startsWith('image/') ? <FileThumbnail file={file} /> : file.file_type === 'application/pdf' ? <PDFThumbnail file={file} /> : <div className="w-full h-full flex items-center justify-center">
                                      {getFileIcon(file.file_type)}
                                    </div>}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{file.file_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {(file.file_size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                            {/* Delete Button */}
                            <div className="absolute top-2 right-2">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 hover:bg-red-100 text-red-600 hover:text-red-700 rounded-full" onClick={e => e.stopPropagation()} title="Datei löschen">
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Datei löschen</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Sind Sie sicher, dass Sie die Datei "{file.file_name}" löschen möchten? 
                                      Diese Aktion kann nicht rückgängig gemacht werden.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => deleteFile(file.id)} className="bg-red-600 hover:bg-red-700">
                                      Löschen
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </Card>;
                        })}
                    </div> : <div className="text-center py-8 bg-muted/30 rounded-lg border-2 border-dashed border-border">
                      <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Noch keine Dokumente hochgeladen
                      </p>
                    </div>}
                </div>
              </CardContent>
            </Card>
          </div>}

        {/* File Preview Modal */}
        {previewFile && <div className={`fixed inset-0 z-50 ${isFullscreen ? 'bg-black' : 'bg-black/50 flex items-center justify-center p-4'}`} onClick={e => {
                // Close when clicking outside the modal (only in normal mode)
                if (!isFullscreen && e.target === e.currentTarget) {
                  URL.revokeObjectURL(previewFile.url);
                  setPreviewFile(null);
                }
              }}>
            
            {isFullscreen ? (
              // Fullscreen Cinema Mode
              <div className="w-full h-full relative flex items-center justify-center">
                {/* Fullscreen Controls */}
                <button onClick={() => {
                     setIsFullscreen(false);
                   }} className="absolute top-6 left-6 z-20 
                           bg-black/70 hover:bg-black/90 text-white rounded-full p-3 
                           transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Vollbild verlassen">
                  <Minimize className="h-6 w-6" />
                </button>

                <button onClick={() => {
                     const a = document.createElement('a');
                     a.href = previewFile.url;
                     a.download = previewFile.name;
                     document.body.appendChild(a);
                     a.click();
                     document.body.removeChild(a);
                   }} className="absolute top-6 right-20 z-20 
                           bg-black/70 hover:bg-black/90 text-white rounded-full p-3 
                           transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Datei herunterladen">
                  <Download className="h-6 w-6" />
                </button>
                
                <button onClick={() => {
                     URL.revokeObjectURL(previewFile.url);
                     setPreviewFile(null);
                     setIsFullscreen(false);
                   }} className="absolute top-6 right-6 z-20 
                           bg-black/70 hover:bg-black/90 text-white rounded-full p-3 
                           transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Schließen">
                  <X className="h-6 w-6" />
                </button>

                {/* Navigation in Fullscreen */}
                {customerFiles.length > 1 && <>
                    <button onClick={() => navigateToFile('prev')} className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 
                               bg-black/70 hover:bg-black/90 text-white rounded-full p-4 
                               transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Vorherige Datei">
                      <ArrowLeft className="h-8 w-8" />
                    </button>
                    
                    <button onClick={() => navigateToFile('next')} className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 
                               bg-black/70 hover:bg-black/90 text-white rounded-full p-4 
                               transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Nächste Datei">
                      <ArrowLeft className="h-8 w-8 rotate-180" />
                    </button>
                  </>}

                {/* File Counter in Fullscreen */}
                {customerFiles.length > 1 && (
                  <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 
                                 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
                    {currentFileIndex + 1} von {customerFiles.length}
                  </div>
                )}

                {/* Content in Fullscreen */}
                <div className="w-full h-full flex items-center justify-center p-8"
                     onTouchStart={onTouchStart}
                     onTouchMove={onTouchMove}
                     onTouchEnd={onTouchEnd}>
                  {previewFile.type.startsWith('image/') ? (
                    <img src={previewFile.url} alt={previewFile.name} className="max-w-full max-h-full object-contain" />
                   ) : previewFile.type === 'application/pdf' ? (
                     <iframe
                       src={`${previewFile.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                       className="w-full h-full border-0"
                       title={previewFile.name}
                       style={{ 
                         overflow: 'hidden',
                         pointerEvents: 'auto'
                       }}
                       scrolling="no"
                     />
                 ) : (
                   <div className="text-center text-white">
                     <FileText className="mx-auto h-32 w-32 text-white/70 mb-8" />
                     <p className="text-xl mb-6">Vorschau für diesen Dateityp nicht verfügbar</p>
                     <Button onClick={() => {
                         const a = document.createElement('a');
                         a.href = previewFile.url;
                         a.download = previewFile.name;
                         document.body.appendChild(a);
                         a.click();
                         document.body.removeChild(a);
                       }} variant="secondary">
                       Datei herunterladen
                     </Button>
                   </div>
                 )}
                </div>
              </div>
            ) : (
              // Normal Modal Mode
              <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
                  <CardHeader className="p-2 md:p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-0">
                        <CardTitle className="flex items-center gap-1 md:gap-2 truncate text-sm md:text-lg">
                          <FileText className="h-3 w-3 md:h-5 md:w-5 flex-shrink-0" />
                          <span className="truncate text-xs md:text-base">{previewFile.name}</span>
                        </CardTitle>
                        {customerFiles.length > 1 && <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-muted-foreground">
                            <span>{currentFileIndex + 1} von {customerFiles.length}</span>
                          </div>}
                      </div>
                       <div className="flex gap-1 md:gap-2">
                       </div>
                    </div>
                  </CardHeader>
                   <CardContent className="p-0 relative">
                    {/* Fullscreen Button - Left side */}
                    <button onClick={() => {
                         setIsFullscreen(!isFullscreen);
                       }} className="absolute top-4 left-4 z-10 
                               bg-black/50 hover:bg-black/70 text-white rounded-full p-2 
                               transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Vollbild anzeigen">
                      <Maximize className="h-5 w-5" />
                    </button>

                    {/* Download Button - Next to close button */}
                    <button onClick={() => {
                         const a = document.createElement('a');
                         a.href = previewFile.url;
                         a.download = previewFile.name;
                         document.body.appendChild(a);
                         a.click();
                         document.body.removeChild(a);
                       }} className="absolute top-4 right-16 z-10 
                               bg-black/50 hover:bg-black/70 text-white rounded-full p-2 
                               transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Datei herunterladen">
                      <Download className="h-5 w-5" />
                    </button>
                    
                    {/* Close X Button - Always visible */}
                    <button onClick={() => {
                         URL.revokeObjectURL(previewFile.url);
                         setPreviewFile(null);
                         setIsFullscreen(false);
                       }} className="absolute top-4 right-4 z-10 
                               bg-black/50 hover:bg-black/70 text-white rounded-full p-2 
                               transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Vorschau schließen">
                      <X className="h-5 w-5" />
                    </button>
                   
                   {/* Navigation Overlay Arrows - Only when multiple files */}
                   {customerFiles.length > 1 && <>
                       {/* Left Arrow - Previous */}
                       <button onClick={() => navigateToFile('prev')} className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 
                                  bg-black/50 hover:bg-black/70 text-white rounded-full p-3 
                                  transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Vorherige Datei">
                         <ArrowLeft className="h-6 w-6" />
                       </button>
                       
                       {/* Right Arrow - Next */}
                       <button onClick={() => navigateToFile('next')} className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 
                                  bg-black/50 hover:bg-black/70 text-white rounded-full p-3 
                                  transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50" aria-label="Nächste Datei">
                         <ArrowLeft className="h-6 w-6 rotate-180" />
                       </button>
                     </>}
                     <div className="max-h-[70vh] overflow-auto"
                          onTouchStart={onTouchStart}
                          onTouchMove={onTouchMove}
                          onTouchEnd={onTouchEnd}>
                       {previewFile.type.startsWith('image/') ? (
                         <img src={previewFile.url} alt={previewFile.name} className="w-full h-auto max-h-full object-contain" />
                        ) : previewFile.type === 'application/pdf' ? (
                          <div className="w-full h-[70vh]">
                            <iframe
                              src={`${previewFile.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                              className="w-full h-full border-0"
                              title={previewFile.name}
                              style={{ 
                                overflow: 'hidden',
                                pointerEvents: 'auto'
                              }}
                              scrolling="no"
                            />
                          </div>
                      ) : previewFile.type.startsWith('text/') ? (
                        <div className="p-6 w-full h-[60vh] overflow-auto">
                          <div className="w-full h-full bg-background border border-border rounded p-4">
                            <pre className="whitespace-pre-wrap text-sm font-mono">{/* Load text content */}</pre>
                            <div className="text-center text-muted-foreground">
                              <p className="mb-4">Textdatei-Vorschau</p>
                              <Button onClick={() => {
                                  window.open(previewFile.url, '_blank', 'noopener,noreferrer');
                                }}>
                                In neuem Tab öffnen
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-6 text-center">
                          <FileText className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                          <p className="text-muted-foreground mb-4">
                            Vorschau für diesen Dateityp nicht verfügbar
                          </p>
                          <Button onClick={() => {
                              const a = document.createElement('a');
                              a.href = previewFile.url;
                              a.download = previewFile.name;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                            }}>
                            Datei herunterladen
                          </Button>
                        </div>
                      )}
                     </div>
                 </CardContent>
              </Card>
            )}
          </div>}

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
          <DialogContent className="w-full max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Kunde löschen
              </DialogTitle>
              <DialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="font-medium">
                  Möchten Sie <span className="text-destructive">{selectedCustomer?.name}</span> wirklich löschen?
                </p>
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                  <p className="text-sm text-destructive font-medium mb-2">
                    ⚠️ Diese Aktion ist unwiderruflich!
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Alle Kundendaten werden gelöscht</li>
                    <li>• Alle hochgeladenen Dokumente werden entfernt</li>
                    <li>• Alle Aufgaben und Notizen gehen verloren</li>
                    <li>• Die Bedarfsanalyse wird gelöscht</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirmation(false)} className="flex-1">
                  Abbrechen
                </Button>
                <Button variant="destructive" onClick={handleDeleteCustomer} className="flex-1 gap-2">
                  <Trash2 className="h-4 w-4" />
                  Endgültig löschen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Appointment Dialog */}
      {selectedCustomer && (
        <CustomerAppointmentDialog
          open={showAppointmentDialog}
          onOpenChange={setShowAppointmentDialog}
          onConfirm={handleCreateAppointment}
          customerName={selectedCustomer.name || `${selectedCustomer.first_name} ${selectedCustomer.last_name || ''}`}
        />
      )}

      {/* Scroll to top button */}
      {showScrollToTop && (
        <Button
          variant="default"
          size="icon"
          className="fixed bottom-6 right-6 z-50 rounded-full shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 animate-scale-in"
          onClick={scrollToTop}
          title="Nach oben scrollen"
        >
          <ChevronUp className="h-5 w-5" />
        </Button>
      )}
    </SidebarProvider>
  );
};

export default CustomerManagement;